import { D as requestPasswordResetRequestSchema, E as requestEmailChangeRequestSchema, T as registrationRequestSchema, a as beginRegistrationRequestSchema, l as completeEnrolmentRequestSchema, o as changePasswordRequestSchema, p as createAccountRequestSchema, s as closeAccountRequestSchema, u as completePasswordResetRequestSchema, x as loginRequestSchema } from "./request-schemas-DKZGbaal.mjs";
import "zod";
import { createMutator, createRequestTransport } from "@hurdl/http-client";
import { createMemoryTokenStore } from "@hurdl/session";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Alert, Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, FormField, Input, InstantDisplay, Stack, Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@hurdl/design-system";
import "@hurdl/contracts";
//#region src/frontend/api/transport.ts
/**
* Builds this capability's transport, which every generated operation reaches
* through the mutator in `request.ts`.
*
* **The two arguments are the whole of what this capability contributes.** The
* address resolution, the request, the envelope read and the error map are
* `@hurdl/http-client`'s (R1.12), and this module declares none of them; what it
* declares is that requests to *this* API carry credentials, and where the router is
* mounted.
*
* **`credentials: 'include'` is why the composition has a module of its own** rather
* than sitting inline in the store's constructor. It is the one respect in which this
* capability's transport differs from the Accounts_Capability's, R1.4 exists to make
* that difference visible, and `test/transport-resolution.property.test.ts` holds it
* over every base address and path — a claim about this capability's transport, not
* about the package's, and one that needs something to build.
*
* The browser attaches the httpOnly refresh cookie because of it. The store never
* reads that cookie — it cannot.
*/
function createAuthenticationTransport(configuration) {
	return createRequestTransport({
		baseUrl: configuration.baseUrl,
		credentials: "include",
		...configuration.fetch === void 0 ? {} : { send: configuration.fetch }
	});
}
//#endregion
//#region src/frontend/api/request.ts
/**
* The mutator `@hurdl/http-client` builds, which does the whole of the work:
* it strips the transport out of an operation's options so the transport never
* reaches `fetch`, and raises the message below where an operation was called with
* none.
*
* The message names this client and one of its operations because that is what makes
* it useful, and it is supplied here rather than fixed in the package for the reason
* R1.11 gives: a Platform Package names no capability.
*/
const mutator = createMutator({ absentTransportMessage: "No transport was supplied to the authentication client. Pass one as the `transport` property of the operation options, for example `login(body, { transport })`." });
/**
* The seam between the Generated_Client and the transport.
*
* Every generated operation calls {@link sendRequest} and nothing else, which is
* what makes `src/frontend/generated/` free of a `fetch` call and of a base address
* (R22.6, R22.12): the operation knows its own path and its method, and the transport
* it is handed knows where the router is mounted. The address resolution, the
* request, the envelope read and the error map were all in this module and are all now
* `@hurdl/http-client`'s (R1.12); the transport itself is composed in
* `./transport.ts`.
*
* **Two reasons this is a declared function rather than the `const` the factory
* returns**, and the first is not a matter of taste:
*
* 1. **Orval reads this declaration.** It writes
*    `import { sendRequest } from '../api/request.js'` into the generated tree and
*    types each operation's trailing parameter as
*    `Parameters<typeof sendRequest>[1]` — but only where it can see a second
*    parameter here. Exported as the factory's return value, it emits
*    `options?: RequestInit` instead, which is a different generated file (R1.13
*    requires it be byte-identical) and a narrower type: `RequestInit` has no
*    `transport` property, so every call site in the store would stop compiling.
* 2. It is where the type parameter is named, and naming it keeps the operations'
*    `sendRequest<loginResponse>(…)` form working unchanged.
*
* @throws Error when no transport was supplied, which is a wiring defect rather than
*   a request failure and so carries no status and no platform error code.
*/
async function sendRequest(url, options) {
	return await mutator(url, options);
}
//#endregion
//#region src/frontend/generated/authentication.ts
const getCloseAccountUrl = () => {
	return `/account/close`;
};
/**
* @summary Close the account
*/
const closeAccount = async (closeAccountBody, options) => {
	return sendRequest(getCloseAccountUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(closeAccountBody)
	});
};
const getListAccountsUrl = (params) => {
	const normalizedParams = new URLSearchParams();
	Object.entries(params || {}).forEach(([key, value]) => {
		if (value !== void 0) normalizedParams.append(key, value === null ? "null" : String(value));
	});
	const stringifiedParams = normalizedParams.toString();
	return stringifiedParams.length > 0 ? `/admin/accounts?${stringifiedParams}` : `/admin/accounts`;
};
/**
* @summary List accounts (administrative)
*/
const listAccounts = async (params, options) => {
	return sendRequest(getListAccountsUrl(params), {
		...options,
		method: "GET"
	});
};
const getCreateAccountUrl = () => {
	return `/admin/accounts`;
};
/**
* @summary Create a passwordless account (administrative)
*/
const createAccount = async (createAccountBody, options) => {
	return sendRequest(getCreateAccountUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(createAccountBody)
	});
};
const getListCredentialsUrl = (userId) => {
	return `/admin/accounts/${userId}/credentials`;
};
/**
* @summary List an account’s credentials (administrative or own)
*/
const listCredentials = async (userId, options) => {
	return sendRequest(getListCredentialsUrl(userId), {
		...options,
		method: "GET"
	});
};
const getDisableAccountUrl = (userId) => {
	return `/admin/accounts/${userId}/disable`;
};
/**
* @summary Disable an account (administrative)
*/
const disableAccount = async (userId, disableAccountBody, options) => {
	return sendRequest(getDisableAccountUrl(userId), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(disableAccountBody)
	});
};
const getEnableAccountUrl = (userId) => {
	return `/admin/accounts/${userId}/enable`;
};
/**
* @summary Re-enable a disabled account (administrative)
*/
const enableAccount = async (userId, enableAccountBody, options) => {
	return sendRequest(getEnableAccountUrl(userId), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(enableAccountBody)
	});
};
const getDisableCredentialUrl = (credentialId) => {
	return `/credentials/${credentialId}`;
};
/**
* @summary Disable a credential terminally (administrative or own)
*/
const disableCredential = async (credentialId, options) => {
	return sendRequest(getDisableCredentialUrl(credentialId), {
		...options,
		method: "DELETE"
	});
};
const getWithdrawEmailChangeUrl = () => {
	return `/email/change`;
};
/**
* @summary Give up a pending email change
*/
const withdrawEmailChange = async (options) => {
	return sendRequest(getWithdrawEmailChangeUrl(), {
		...options,
		method: "DELETE"
	});
};
const getGetPendingEmailChangeUrl = () => {
	return `/email/change`;
};
/**
* @summary The address the account is waiting to move to
*/
const getPendingEmailChange = async (options) => {
	return sendRequest(getGetPendingEmailChangeUrl(), {
		...options,
		method: "GET"
	});
};
const getRequestEmailChangeUrl = () => {
	return `/email/change`;
};
/**
* @summary Ask to move the account to another email address
*/
const requestEmailChange = async (requestEmailChangeBody, options) => {
	return sendRequest(getRequestEmailChangeUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(requestEmailChangeBody)
	});
};
const getConfirmEmailChangeUrl = () => {
	return `/email/change/confirm`;
};
/**
* @summary Move the account to the address a token proves
*/
const confirmEmailChange = async (confirmEmailChangeBody, options) => {
	return sendRequest(getConfirmEmailChangeUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(confirmEmailChangeBody)
	});
};
const getVerifyEmailUrl = () => {
	return `/email/verify`;
};
/**
* @summary Prove an email address with a verification token
*/
const verifyEmail = async (verifyEmailBody, options) => {
	return sendRequest(getVerifyEmailUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(verifyEmailBody)
	});
};
const getResendVerificationUrl = () => {
	return `/email/verify/resend`;
};
/**
* @summary Issue a further token proving the account address
*/
const resendVerification = async (resendVerificationBody, options) => {
	return sendRequest(getResendVerificationUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(resendVerificationBody)
	});
};
const getLoginUrl = () => {
	return `/login`;
};
/**
* @summary Start a session
*/
const login = async (loginBody, options) => {
	return sendRequest(getLoginUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(loginBody)
	});
};
const getLogoutUrl = () => {
	return `/logout`;
};
/**
* @summary End the session
*/
const logout = async (logoutBody, options) => {
	return sendRequest(getLogoutUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(logoutBody)
	});
};
const getGetCurrentUserUrl = () => {
	return `/me`;
};
/**
* @summary The authenticated user
*/
const getCurrentUser = async (options) => {
	return sendRequest(getGetCurrentUserUrl(), {
		...options,
		method: "GET"
	});
};
const getChangePasswordUrl = () => {
	return `/password/change`;
};
/**
* @summary Replace the account password
*/
const changePassword = async (changePasswordBody, options) => {
	return sendRequest(getChangePasswordUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(changePasswordBody)
	});
};
const getRequestPasswordResetUrl = () => {
	return `/password/reset`;
};
/**
* @summary Ask for a token that replaces a forgotten password
*/
const requestPasswordReset = async (requestPasswordResetBody, options) => {
	return sendRequest(getRequestPasswordResetUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(requestPasswordResetBody)
	});
};
const getCompletePasswordResetUrl = () => {
	return `/password/reset/confirm`;
};
/**
* @summary Replace a forgotten password with a reset token
*/
const completePasswordReset = async (completePasswordResetBody, options) => {
	return sendRequest(getCompletePasswordResetUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(completePasswordResetBody)
	});
};
const getRefreshUrl = () => {
	return `/refresh`;
};
/**
* @summary Rotate the session and issue an access token
*/
const refresh = async (refreshBody, options) => {
	return sendRequest(getRefreshUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(refreshBody)
	});
};
const getRegisterUrl = () => {
	return `/register`;
};
/**
* @summary Create an account and start a session
*/
const register = async (registerBody, options) => {
	return sendRequest(getRegisterUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(registerBody)
	});
};
const getRevokeOtherSessionsUrl = () => {
	return `/sessions`;
};
/**
* @summary Revoke every session but the current one
*/
const revokeOtherSessions = async (options) => {
	return sendRequest(getRevokeOtherSessionsUrl(), {
		...options,
		method: "DELETE"
	});
};
const getListSessionsUrl = () => {
	return `/sessions`;
};
/**
* @summary The sessions the account holds
*/
const listSessions = async (options) => {
	return sendRequest(getListSessionsUrl(), {
		...options,
		method: "GET"
	});
};
const getRevokeSessionUrl = (sessionId) => {
	return `/sessions/${sessionId}`;
};
/**
* @summary Revoke one session the account holds
*/
const revokeSession = async (sessionId, options) => {
	return sendRequest(getRevokeSessionUrl(sessionId), {
		...options,
		method: "DELETE"
	});
};
const getBeginWebAuthnAssertionUrl = () => {
	return `/webauthn/assertion/begin`;
};
/**
* @summary Begin a passwordless login assertion ceremony
*/
const beginWebAuthnAssertion = async (beginWebAuthnAssertionBody, options) => {
	return sendRequest(getBeginWebAuthnAssertionUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(beginWebAuthnAssertionBody)
	});
};
const getCompleteWebAuthnAssertionUrl = () => {
	return `/webauthn/assertion/complete`;
};
/**
* @summary Complete a passwordless login and start a session
*/
const completeWebAuthnAssertion = async (completeWebAuthnAssertionBody, options) => {
	return sendRequest(getCompleteWebAuthnAssertionUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(completeWebAuthnAssertionBody)
	});
};
const getBeginWebAuthnEnrolmentUrl = () => {
	return `/webauthn/enrolment/begin`;
};
/**
* @summary Begin enrolling a bootstrap account's first key with a ticket
*/
const beginWebAuthnEnrolment = async (beginWebAuthnEnrolmentBody, options) => {
	return sendRequest(getBeginWebAuthnEnrolmentUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(beginWebAuthnEnrolmentBody)
	});
};
const getCompleteWebAuthnEnrolmentUrl = () => {
	return `/webauthn/enrolment/complete`;
};
/**
* @summary Complete enrolling a bootstrap account's first key, spending the ticket
*/
const completeWebAuthnEnrolment = async (completeWebAuthnEnrolmentBody, options) => {
	return sendRequest(getCompleteWebAuthnEnrolmentUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(completeWebAuthnEnrolmentBody)
	});
};
const getBeginWebAuthnRegistrationUrl = () => {
	return `/webauthn/registration/begin`;
};
/**
* @summary Begin registering a WebAuthn credential for the current account
*/
const beginWebAuthnRegistration = async (beginWebAuthnRegistrationBody, options) => {
	return sendRequest(getBeginWebAuthnRegistrationUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(beginWebAuthnRegistrationBody)
	});
};
const getCompleteWebAuthnRegistrationUrl = () => {
	return `/webauthn/registration/complete`;
};
/**
* @summary Complete registering a WebAuthn credential for the current account
*/
const completeWebAuthnRegistration = async (completeWebAuthnRegistrationBody, options) => {
	return sendRequest(getCompleteWebAuthnRegistrationUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options?.headers
		},
		body: JSON.stringify(completeWebAuthnRegistrationBody)
	});
};
//#endregion
//#region src/frontend/webauthn-browser.ts
/**
* Reads the credential handle out of an assertion response.
*
* The passwordless completion route names the credential the authenticator asserted —
* `completeWebAuthnAssertion`'s `credentialId` — and the authenticator reports it on the
* response as `id`, a base64url string. This is the one field anything above the seam
* reads out of a {@link CeremonyResponse}, and it is read here rather than in the store
* so the store stays free of the response's shape.
*
* @throws Error where the response carries no string `id`, which an assertion from a
*   conformant authenticator always does — a response without one is a fault worth
*   surfacing rather than a login to attempt with an empty handle.
*/
function assertionCredentialId(response) {
	const id = response.id;
	if (typeof id !== "string" || id.length === 0) throw new Error("The authenticator returned an assertion carrying no credential identifier.");
	return id;
}
/**
* The real seam over `@simplewebauthn/browser`.
*
* Each method forwards to the library's `optionsJSON`-shaped call and returns the JSON
* the authenticator produced. The opaque {@link CeremonyPublicKeyOptions} the seam takes
* is the server's own ceremony options, which the capability's backend builds to the
* WebAuthn specification the library consumes — so the one assertion here is that this
* record is that shape. It is confined to this module, which is the point of the seam:
* the cast is the single place a library type meets a server-shaped value, and nothing
* above it holds either type. The returned `RegistrationResponseJSON` /
* `AuthenticationResponseJSON` are structurally the opaque record this seam promises, so
* the return flows out with no cast.
*/
const webAuthnBrowser = {
	register: async (options) => {
		return await startRegistration({ optionsJSON: options });
	},
	authenticate: async (options) => {
		return await startAuthentication({ optionsJSON: options });
	}
};
//#endregion
//#region src/frontend/authentication-store.ts
/**
* Refresh the access token this many milliseconds before it expires, so an
* in-flight request does not fail on a token that lapsed mid-journey.
*/
const EXPIRY_MARGIN_MS = 3e4;
/**
* Reads a generated operation's result as the payload it actually is.
*
* **The one place a generated result is cast** (R16.8, and the capability's
* `AGENTS.md` records why it must be). The generated operations type their result as
* Orval's `{ data, status, headers }` triple, which is what Orval's own fetch client
* returns; every operation here goes through the mutator in `api/request.ts`, which
* hands back whatever `@hurdl/http-client`'s transport resolved with, and that is the
* payload — the `{ data }` envelope having already been read and any status at or
* above 400 having already been raised as `ApiRequestError`. The generated types
* cannot describe that, so the correction is made here and nowhere else.
*
* One reader for every payload rather than one per shape, because a payload's shape
* does not change the correction, and a templated path does not either:
* `DELETE /sessions/{sessionId}` is mis-typed exactly as every other operation is.
*
* **It checks nothing, and that is a change from the reader it replaces.** The
* envelope check moved into `@hurdl/http-client` with the envelope read, where the
* distinction it exists for still holds: a `data` of `null` — the answer
* `GET /email/change` gives an account with nothing pending — resolves as `null`, and
* a successful body carrying no envelope at all raises there instead. The first is a
* state of a working account; the second is a defect, and reading it as "nothing
* pending" would hide it.
*/
function readData(response) {
	return response;
}
/**
* Client-side authentication state.
*
* The access token is held by a `TokenStore` and never persisted. `initialise`
* recovers a session by one of two paths — a token the `TokenStore` already
* holds, or the httpOnly refresh cookie — so a browser reload, which always
* starts with an empty token store, looks seamless without a readable token ever
* existing.
*
* Every request goes through the Orval-generated operations, which route through
* the single transport this store builds from its configuration. The store
* constructs no URL and calls no `fetch` of its own, save the one in
* `authorisedFetch`, which proxies arbitrary URLs belonging to other
* capabilities rather than operations of this API.
*
* Framework-agnostic; the React bindings are a thin layer over `subscribe` and
* `getSnapshot`.
*/
var AuthenticationStore = class {
	tokens;
	webAuthn;
	snapshot = {
		status: "unknown",
		user: null
	};
	listeners = /* @__PURE__ */ new Set();
	expiresAt = 0;
	/**
	* The identifier of the session this store is holding, as the last session
	* listing reported it, or `null` where no listing has said.
	*
	* This is how {@link revokeSession} recognises that the session it has just
	* ended is its own (R16.5), and it is the only honest source available: a
	* `SessionDescriptor` carries `current`, which the capability decides from the
	* `sessionId` of the Access_Token_Claims and from nothing else (R10.8), so the
	* identifier recorded here is the backend's own answer to "which of these is the
	* caller's?" rather than anything this store inferred. It is never derived from a
	* descriptor's address or user agent: two sessions started from one browser carry
	* identical metadata, and a person shown the wrong one as theirs would revoke the
	* wrong one.
	*
	* No contract change is needed for it. `AuthenticationResult` reports no session
	* identifier and `GET /me` reports none either, but a caller can only name a
	* session `GET /sessions` showed it — that is what the route's own parameter
	* description says the identifier is — so by the time a revocation is possible a
	* listing has already named the current session.
	*
	* It is not the cached session list R16.6 forbids. What is kept is one fact about
	* the session this store holds, which the store already owns; the descriptors
	* themselves are handed to the caller and dropped.
	*
	* Reset to `null` wherever the session this store holds may have changed: by
	* {@link reset}, and at the start of {@link login} and {@link register}, each of
	* which begins a session whose identifier no listing has named yet. A refresh
	* deliberately does not clear it, because rotation replaces the credential and
	* keeps the session, identifier included.
	*
	* The residual case is a caller that revokes its own session having never listed
	* one, which this store cannot recognise. It keeps reporting `authenticated`
	* until the next rotation finds the credential gone and reports `anonymous`,
	* which is the same recovery a session revoked from another device gets.
	*/
	currentSessionId = null;
	/**
	* The in-flight refresh, if any. Shared between callers so that several
	* simultaneous 401s trigger one rotation rather than a race in which all but
	* one credential is invalidated.
	*/
	pendingRefresh = null;
	/**
	* The one place in the frontend that joins a URL or calls `fetch` for an
	* operation of this API. Passed to each generated operation per call, so two
	* stores may address two base addresses with no module-level state.
	*/
	transport;
	constructor(configuration, tokens = createMemoryTokenStore(), webAuthn = webAuthnBrowser) {
		this.tokens = tokens;
		this.webAuthn = webAuthn;
		this.transport = createAuthenticationTransport(configuration);
	}
	getSnapshot() {
		return this.snapshot;
	}
	subscribe(listener) {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}
	/**
	* Attempts to restore a session, by one of two declared paths.
	*
	* Where the injected `TokenStore` already holds an access token, the store
	* identifies its holder through the `GET /me` operation and reports
	* `authenticated` without rotating the session for a credential it already has.
	* Where it holds none — the default `createMemoryTokenStore()`, and every
	* browser reload, among them — the store recovers from the httpOnly refresh
	* cookie instead.
	*
	* A held token the API refuses falls through to the cookie. The fallback is the
	* point of taking the first path at all: a token left behind by an expired or
	* revoked session cannot strand the store in `anonymous` when a valid cookie
	* would have restored the session.
	*
	* Call once when the application mounts. Resolves to `anonymous` rather than
	* throwing when neither path finds a session, since that is the normal case for
	* a first-time visitor.
	*/
	async initialise() {
		if (this.snapshot.status !== "unknown") return;
		this.set({
			status: "authenticating",
			user: this.snapshot.user
		});
		if (await this.identifyFromHeldToken()) return;
		await this.refreshAccessToken();
	}
	async login(request) {
		this.currentSessionId = null;
		this.set({
			status: "authenticating",
			user: null
		});
		try {
			this.accept(readData(await login(request, { transport: this.transport })));
		} catch (error) {
			this.reset();
			throw error;
		}
	}
	async register(request) {
		this.currentSessionId = null;
		this.set({
			status: "authenticating",
			user: null
		});
		try {
			this.accept(readData(await register(request, { transport: this.transport })));
		} catch (error) {
			this.reset();
			throw error;
		}
	}
	async logout() {
		try {
			await logout({}, { transport: this.transport });
		} finally {
			this.reset();
		}
	}
	/**
	* Proves an email address with a Verification_Token (R16.11).
	*
	* Unauthenticated: a person following a link from an inbox holds no session, so a
	* product calls this from the page the link landed on without knowing whether
	* anybody is signed in, and needs to know nothing about which routes carry a
	* bearer token.
	*
	* The token is passed and not kept (R16.7).
	*
	* The route answers with the account as it now stands, and this store adopts it
	* where it describes the account being held (R16.2), so a rendered prompt to
	* verify disappears without a further read. Where it describes another account —
	* a person signed in on one account following a link proving another's — nothing
	* is adopted: {@link adoptUser} is what decides that.
	*/
	async verifyEmail(token) {
		this.adoptUser(readData(await verifyEmail({ token }, { transport: this.transport })));
	}
	/**
	* Asks for a further token proving the account's own address.
	*
	* The token reaches the person through the address being proven and never
	* through the channel that asked for it, so there is nothing to return, and the
	* account is unchanged by asking, so there is nothing to update.
	*/
	async resendVerification() {
		await resendVerification({}, await this.authorisedOptions());
	}
	/**
	* Replaces the account password, the current one proving who is acting.
	*
	* The session is retained (R16.3): the backend revokes every session but the
	* acting one, which is the session this store's access token names, so the token
	* it holds stays usable and the snapshot stays as it is. `Public_User` carries
	* nothing a password change alters, so there is nothing to adopt either.
	*/
	async changePassword(request) {
		await changePassword(request, await this.authorisedOptions());
	}
	/**
	* Asks to move the account to another address, and reports the change recorded.
	*
	* The Login_Identifier is untouched until the token proving the new address is
	* consumed, so what comes back is the pending change rather than a new user, and
	* the snapshot is left alone: a pending address is deliberately no member of
	* `Public_User`, and asking for a change spares every session, this store's
	* included.
	*/
	async requestEmailChange(request) {
		return readData(await requestEmailChange(request, await this.authorisedOptions()));
	}
	/**
	* Moves the account to the address a token proves.
	*
	* Unauthenticated, so a change asked for on a laptop can be confirmed from a
	* phone. The access token is nonetheless attached where this store holds one,
	* because the route reads the claims of a confirmation that presents them and the
	* session named there is the one the change spares (R15.2). Where the store is
	* not authenticated nothing is attached and nothing is refreshed: the token in
	* the body is the credential.
	*
	* The session is retained (R16.3) and the account on its new address is adopted
	* (R16.2): the confirmation spares the session it presented, which is this
	* store's where it attached one, and the response carries the new
	* Login_Identifier recorded verified. A confirmation arriving with no session
	* spares none — but it names an account this store is not holding, and
	* {@link adoptUser} declines a user that is not the one in the snapshot, so a
	* signed-out store stays anonymous rather than adopting a user it holds no token
	* for.
	*/
	async confirmEmailChange(token) {
		this.adoptUser(readData(await confirmEmailChange({ token }, await this.optionallyAuthorisedOptions())));
	}
	/**
	* The address the account is waiting to move to, or `null` where none is.
	*
	* `null` is an ordinary answer rather than a failure: an account with nothing
	* pending is a working account, and the route says so with a 200.
	*/
	async getPendingEmailChange() {
		return readData(await getPendingEmailChange(await this.authorisedOptions()));
	}
	/**
	* Gives up a pending email change, releasing the address it was holding.
	*
	* Changes neither `Public_User` nor any session, so the snapshot is untouched: a
	* withdrawal undoes a request that never moved the Login_Identifier.
	*/
	async withdrawEmailChange() {
		await withdrawEmailChange(await this.authorisedOptions());
	}
	/**
	* Asks for a token that replaces a forgotten password.
	*
	* Unauthenticated, and it names an address rather than an account: a person who
	* cannot remember their password cannot be signed in. It resolves identically
	* whether or not the address is registered, which is what the component built
	* over it renders one message for.
	*/
	async requestPasswordReset(email) {
		await requestPasswordReset({ email }, { transport: this.transport });
	}
	/**
	* Replaces a forgotten password with the token from a link.
	*
	* Unauthenticated, and it starts no session and returns no token: the completion
	* revokes every session the account held, so what follows is a login. The token
	* is passed and not kept (R16.7).
	*
	* The store is nonetheless **not** cleared, and the reason is that it cannot
	* honestly conclude its own session was among those revoked. The token names an
	* account and the call carries no session of its own, so a store that happens to
	* be holding a session may be holding one for another account entirely — and
	* clearing on that would sign a person out of an account a reset did not touch.
	* Where the account is the one being held, the revocation is invisible here until
	* the next rotation finds the credential gone and reports `anonymous`, which is
	* the recovery any externally revoked session already gets. R16.4 and R16.5 name
	* the two acts this store can be certain about, and both are certain because the
	* account is the one its own access token names.
	*/
	async completePasswordReset(token, newPassword) {
		await completePasswordReset({
			token,
			newPassword
		}, { transport: this.transport });
	}
	/**
	* The sessions the account holds, as Session_Descriptors.
	*
	* Returned to the caller and held nowhere (R16.6): a list is a point-in-time
	* answer to a question a person asked, and one kept in the snapshot would go on
	* showing a revoked session as active.
	*
	* The one thing kept is the identifier of whichever descriptor the capability
	* marked `current`, which is {@link currentSessionId} and which is a fact about
	* the session this store holds rather than a copy of the list.
	*/
	async listSessions() {
		const sessions = readData(await listSessions(await this.authorisedOptions()));
		this.currentSessionId = sessions.find((session) => session.current)?.id ?? null;
		return sessions;
	}
	/**
	* Revokes one session the account holds, named as `listSessions` reports its id.
	*
	* The identifier is passed to the generated operation, which substitutes it into
	* the path: this store holds no URL (the capability's invariant 11).
	*
	* Where the session revoked is the one this store is holding, the store clears
	* itself and reports `anonymous` (R16.5): the credential it held has just been
	* destroyed, and the transport half of the same act clears the refresh cookie, so
	* there is nothing left to recover a session from. It knows which session is its
	* own from {@link currentSessionId}, whose documentation records why that is the
	* one honest source and what the case it cannot recognise costs.
	*
	* Revoking any other session leaves this store exactly as it was: the credential
	* it holds is untouched and no `Public_User` field changed.
	*/
	async revokeSession(sessionId) {
		await revokeSession(sessionId, await this.authorisedOptions());
		if (sessionId === this.currentSessionId) this.reset();
	}
	/**
	* Revokes every session but the one this store is holding, and reports how many
	* were ended, so a person who has just signed out four devices can be told it was
	* four.
	*
	* The session this store holds is the one spared, so nothing is cleared and the
	* count is answered to the caller rather than kept: it describes an act that has
	* finished, and a number held in the snapshot would still be four after the fifth
	* device signed in.
	*/
	async revokeOtherSessions() {
		return readData(await revokeOtherSessions(await this.authorisedOptions())).revokedSessionCount;
	}
	/**
	* Closes the account, the password proving who is acting.
	*
	* The store is cleared and reports `anonymous` (R16.4). Closure revokes every
	* session the account held, this store's among them, and there is no reopening: an
	* access token issued moments earlier stays verifiable until it expires, so a
	* store that kept reporting `authenticated` would be showing a person an account
	* they have just closed.
	*/
	async closeAccount(currentPassword) {
		await closeAccount({ currentPassword }, await this.authorisedOptions());
		this.reset();
	}
	/**
	* Logs in with a passkey alone — the passwordless assertion ceremony (R2.2).
	*
	* Begins the ceremony (`beginWebAuthnAssertion`), hands the server's options to the
	* authenticator through the browser seam, and completes it
	* (`completeWebAuthnAssertion`) with the challenge handle, the credential the
	* authenticator chose and the assertion response. A verifying assertion issues the
	* ordinary session, so the result is accepted exactly as a password login's is: the
	* access token is written and the snapshot reports `authenticated`.
	*
	* The sole factor is the key — no password is asked for and none is held — which is
	* what the Admin_Panel's login composes. A rejection at any step clears the store, as
	* a password login's does, and reaches the caller as the raised error.
	*/
	async authenticateWithPasskey() {
		this.currentSessionId = null;
		this.set({
			status: "authenticating",
			user: null
		});
		try {
			const options = readData(await beginWebAuthnAssertion({}, { transport: this.transport }));
			const response = await this.webAuthn.authenticate(options.publicKey);
			this.accept(readData(await completeWebAuthnAssertion({
				challengeId: options.challengeId,
				credentialId: assertionCredentialId(response),
				response
			}, { transport: this.transport })));
		} catch (error) {
			this.reset();
			throw error;
		}
	}
	/**
	* Registers a passkey against the signed-in account — the self-registration ceremony
	* (R1.6).
	*
	* Begins the ceremony (`beginWebAuthnRegistration`) with the label the operator chose,
	* drives the authenticator through the browser seam, and completes it
	* (`completeWebAuthnRegistration`) with the challenge handle, the attestation response
	* and the label. Both routes are bearer, so the account the key binds to is the one
	* the access token names — the service judges the target, never a body field.
	*
	* The session is untouched: a key is registered, not a session begun, so the snapshot
	* is left exactly as it was. A rejection reaches the caller as the raised error.
	*/
	async registerPasskey(label) {
		const options = readData(await beginWebAuthnRegistration({ label }, await this.authorisedOptions()));
		const response = await this.webAuthn.register(options.publicKey);
		await completeWebAuthnRegistration({
			challengeId: options.challengeId,
			response,
			label
		}, await this.authorisedOptions());
	}
	/**
	* Enrols the first key of a bootstrap account, authorised by a one-time ticket rather
	* than a session (R8.1).
	*
	* **Unauthenticated throughout**: a bootstrap account is passwordless and keyless and so
	* has no session, and the ticket — the handle a person carries from the bootstrap
	* output — is the credential. The calls go through the bare transport, not
	* {@link authorisedOptions}, exactly as the passwordless assertion's do, because there
	* is no access token to carry.
	*
	* Begins the ceremony with the ticket (`beginWebAuthnEnrolment`), drives the
	* authenticator through the browser seam, and completes it (`completeWebAuthnEnrolment`)
	* with the ticket, the challenge handle, the attestation response and the label — the
	* complete step spending the ticket. **No session is begun**: a key is enrolled, so the
	* snapshot is left exactly as it was, and the operator then signs in through the ordinary
	* passwordless login. A rejection reaches the caller as the raised error.
	*/
	async enrolFirstKey(ticket, label) {
		const options = readData(await beginWebAuthnEnrolment({ ticket }, { transport: this.transport }));
		const response = await this.webAuthn.register(options.publicKey);
		await completeWebAuthnEnrolment({
			ticket,
			challengeId: options.challengeId,
			response,
			label
		}, { transport: this.transport });
	}
	/**
	* Creates a passwordless account for another person (R5.1), gated administratively by
	* `ACCOUNT_CREATE_ANY` at the route.
	*
	* Answers with the created account's summary and touches no snapshot of this store: an
	* operator creating an account is not signing into it.
	*/
	async createAccount(email) {
		return readData(await createAccount({ email }, await this.authorisedOptions()));
	}
	/**
	* A page of accounts (R5.4), gated administratively by `ACCOUNT_LIST_ANY`.
	*
	* Answered to the caller and held nowhere, as the session listing is (R16.6). The
	* page carries the material-free {@link AccountSummary}s and the total, so a caller
	* pages without this store keeping a copy that would go stale the first time an
	* account was disabled.
	*/
	async listAccounts(request = {}) {
		const page = readData(await listAccounts({
			...request.page === void 0 ? {} : { page: request.page },
			...request.pageSize === void 0 ? {} : { pageSize: request.pageSize }
		}, await this.authorisedOptions()));
		return {
			items: page.items,
			total: page.total
		};
	}
	/**
	* Disables an account (R5.2), gated administratively by `ACCOUNT_DISABLE_ANY`.
	*
	* Blocks the account's login and revokes its sessions at the service; this store's own
	* snapshot is untouched, an operator disabling an account not being that account.
	*/
	async disableAccount(userId) {
		await disableAccount(userId, {}, await this.authorisedOptions());
	}
	/** Re-enables a disabled account (R5.3), gated by `ACCOUNT_DISABLE_ANY`. */
	async enableAccount(userId) {
		await enableAccount(userId, {}, await this.authorisedOptions());
	}
	/**
	* The credentials an account holds, as material-free {@link CredentialDescriptor}s
	* (R7.3).
	*
	* Reachable by the holder, or administratively by `CREDENTIAL_DISABLE_ANY`. Answered
	* to the caller and held nowhere, as the other listings are.
	*/
	async listCredentials(userId) {
		return readData(await listCredentials(userId, await this.authorisedOptions()));
	}
	/**
	* Disables a credential terminally (R7.5), by the holder or by `CREDENTIAL_DISABLE_ANY`.
	*
	* Permitted even where it leaves a passwordless account with no usable key (R7.4):
	* recovery is a fresh registration. Touches no snapshot of this store.
	*/
	async disableCredential(credentialId) {
		await disableCredential(credentialId, await this.authorisedOptions());
	}
	/**
	* Returns a usable access token, refreshing first if the current one is
	* missing or close to expiry.
	*/
	async getAccessToken() {
		const token = this.tokens.read();
		if (token !== null && Date.now() < this.expiresAt - EXPIRY_MARGIN_MS) return token;
		return await this.refreshAccessToken();
	}
	/**
	* Performs a request with the access token attached, refreshing and retrying
	* once if the server reports the token is no longer valid.
	*
	* Other capabilities should call APIs through this rather than reading the
	* token themselves. It calls `fetch` directly because the URL it is given
	* belongs to another capability and is no operation of this API.
	*/
	async authorisedFetch(input, init = {}) {
		const response = await this.send(input, init, await this.getAccessToken());
		if (response.status !== 401) return response;
		const refreshed = await this.refreshAccessToken();
		if (refreshed === null) return response;
		return await this.send(input, init, refreshed);
	}
	/**
	* The options a generated operation on a bearer route is called with: this
	* store's transport, and the access token in an `Authorization` header.
	*
	* The token comes from {@link getAccessToken}, so a session whose token has
	* lapsed or is close to lapsing is rotated before the request rather than after
	* a refusal. Where there is no session to rotate the request is made all the
	* same, with no header: the route answers 401 and the caller is raised the
	* capability's mapped error, which is the same answer a revoked session gets
	* and the one a component is written to render. Deciding here that the caller
	* is anonymous would substitute a local error for a mapped one.
	*/
	async authorisedOptions() {
		const token = await this.getAccessToken();
		if (token === null) return { transport: this.transport };
		return {
			transport: this.transport,
			headers: { authorization: `Bearer ${token}` }
		};
	}
	/**
	* The options for a route that reads the Access_Token_Claims where they are
	* present and refuses nothing for want of them (R15.2), which is
	* `POST /email/change/confirm` alone.
	*
	* A held token is attached, so the session the confirmation arrived on is the one
	* the change spares. A store reporting anything but `authenticated` attaches
	* nothing and — unlike {@link authorisedOptions} — asks for no rotation: a person
	* following a link while signed out has no session to recover, and an attempt to
	* recover one would put a refusal in front of a request the route was written to
	* accept.
	*/
	async optionallyAuthorisedOptions() {
		if (this.snapshot.status !== "authenticated") return { transport: this.transport };
		return await this.authorisedOptions();
	}
	async send(input, init, token) {
		const headers = new Headers(init.headers);
		if (token !== null) headers.set("authorization", `Bearer ${token}`);
		return await fetch(input, {
			...init,
			headers,
			credentials: "include"
		});
	}
	/**
	* The first of the two recovery paths {@link initialise} declares: identifies
	* the user from a token the `TokenStore` already held, through the `GET /me`
	* operation.
	*
	* Returns `false` both when no token is held and when the token is refused, in
	* either case leaving the caller to recover from the refresh cookie instead. It
	* therefore reports failure rather than raising: a refused token is an expected
	* outcome, not an error.
	*
	* `expiresAt` is deliberately left at zero, because `GET /me` reports no expiry
	* for the token it accepted. A zero expiry reads as already lapsed, so the first
	* {@link getAccessToken} rotates and learns the real one rather than trusting a
	* token whose remaining life is unknown.
	*/
	async identifyFromHeldToken() {
		const token = this.tokens.read();
		if (token === null) return false;
		try {
			const user = readData(await getCurrentUser({
				transport: this.transport,
				headers: { authorization: `Bearer ${token}` }
			}));
			this.set({
				status: "authenticated",
				user
			});
			return true;
		} catch {
			return false;
		}
	}
	async refreshAccessToken() {
		this.pendingRefresh ??= this.performRefresh();
		try {
			return await this.pendingRefresh;
		} finally {
			this.pendingRefresh = null;
		}
	}
	async performRefresh() {
		try {
			const result = readData(await refresh({}, { transport: this.transport }));
			this.accept(result);
			return result.accessToken;
		} catch {
			this.reset();
			return null;
		}
	}
	/**
	* Adopts a `Public_User` an operation answered with, where it describes the
	* account this store is holding, and notifies subscribers (R16.2).
	*
	* Two operations answer with a user rather than starting a session — proving an
	* address and confirming an email change — and both are unauthenticated, so the
	* user that comes back is not necessarily this store's. It is adopted only where
	* the store reports `authenticated` and the identifiers match, which leaves the
	* two cases that would otherwise be wrong untouched: a signed-out store would
	* report `authenticated` for an account it holds no token for, and a store signed
	* in on one account would replace the user it is rendering with another's.
	*
	* The session is untouched either way. Both acts spare the session that presented
	* the token, so the access token held here is still the token of a live session
	* and only the user it describes has moved on.
	*/
	adoptUser(user) {
		if (this.snapshot.status !== "authenticated" || this.snapshot.user?.id !== user.id) return;
		this.set({
			status: "authenticated",
			user
		});
	}
	accept(result) {
		this.tokens.write(result.accessToken);
		this.expiresAt = Date.parse(result.accessTokenExpiresAt);
		this.set({
			status: "authenticated",
			user: result.user
		});
	}
	reset() {
		this.tokens.clear();
		this.expiresAt = 0;
		this.currentSessionId = null;
		this.set({
			status: "anonymous",
			user: null
		});
	}
	set(snapshot) {
		this.snapshot = snapshot;
		for (const listener of this.listeners) listener();
	}
};
function createAuthenticationStore(configuration, tokens, webAuthn) {
	return new AuthenticationStore(configuration, tokens, webAuthn);
}
//#endregion
//#region src/frontend/react/authentication-context.ts
const AuthenticationContext = createContext(null);
//#endregion
//#region src/frontend/react/authentication-provider.tsx
/**
* Provides authentication state to the tree and attempts to restore a session
* on mount.
*
* Place it above anything that reads authentication state, typically at the
* application root:
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <App />
* </AuthenticationProvider>
* ```
*/
function AuthenticationProvider({ children, baseUrl = "/authentication", store }) {
	const resolved = useMemo(() => store ?? createAuthenticationStore({ baseUrl }), [store, baseUrl]);
	useEffect(() => {
		resolved.initialise();
	}, [resolved]);
	return /* @__PURE__ */ jsx(AuthenticationContext.Provider, {
		value: resolved,
		children
	});
}
//#endregion
//#region src/frontend/react/use-authentication.ts
/**
* Reads authentication state and exposes the actions that change it.
*
* Deliberately does not expose the access token, the account lifecycle included
* (R16.9). Callers that need to reach an authenticated API should use
* `authorisedFetch`, which keeps token handling in one place — and an account
* operation of this API needs no token from a caller at all, the store attaching one
* to each route that reads it, so a component changing a password or listing
* sessions never holds a token either.
*
* Every action is a thin delegation to the store the provider supplied. The whole
* returned value is one memoised object keyed on the snapshot and the store, so a
* given action keeps its identity for as long as the reported state does and every
* action changes identity together when that state moves. That is the convention
* the three original actions were written to and the new ones follow it: a
* per-action `useCallback` would buy nothing while the object around it is rebuilt
* anyway.
*
* @throws Error when used outside an `AuthenticationProvider`.
*/
function useAuthentication() {
	const store = useContext(AuthenticationContext);
	if (store === null) throw new Error("useAuthentication must be used within an AuthenticationProvider");
	const snapshot = useSyncExternalStore(useCallback((listener) => store.subscribe(listener), [store]), () => store.getSnapshot(), () => store.getSnapshot());
	return useMemo(() => ({
		status: snapshot.status,
		user: snapshot.user,
		authenticated: snapshot.status === "authenticated",
		loading: snapshot.status === "unknown" || snapshot.status === "authenticating",
		login: (request) => store.login(request),
		register: (request) => store.register(request),
		logout: () => store.logout(),
		verifyEmail: (token) => store.verifyEmail(token),
		resendVerification: () => store.resendVerification(),
		changePassword: (request) => store.changePassword(request),
		requestEmailChange: (request) => store.requestEmailChange(request),
		confirmEmailChange: (token) => store.confirmEmailChange(token),
		getPendingEmailChange: () => store.getPendingEmailChange(),
		withdrawEmailChange: () => store.withdrawEmailChange(),
		requestPasswordReset: (email) => store.requestPasswordReset(email),
		completePasswordReset: (token, newPassword) => store.completePasswordReset(token, newPassword),
		listSessions: () => store.listSessions(),
		revokeSession: (sessionId) => store.revokeSession(sessionId),
		revokeOtherSessions: () => store.revokeOtherSessions(),
		closeAccount: (currentPassword) => store.closeAccount(currentPassword),
		authenticateWithPasskey: () => store.authenticateWithPasskey(),
		registerPasskey: (label) => store.registerPasskey(label),
		enrolFirstKey: (ticket, label) => store.enrolFirstKey(ticket, label),
		createAccount: (email) => store.createAccount(email),
		listAccounts: (request) => store.listAccounts(request),
		disableAccount: (userId) => store.disableAccount(userId),
		enableAccount: (userId) => store.enableAccount(userId),
		listCredentials: (userId) => store.listCredentials(userId),
		disableCredential: (credentialId) => store.disableCredential(credentialId),
		authorisedFetch: (input, init) => store.authorisedFetch(input, init)
	}), [snapshot, store]);
}
//#endregion
//#region src/frontend/validation.ts
/**
* The login form's rules: the `POST /login` Request_Schema with the
* registration schema's address rule in place of its own.
*
* The route deliberately accepts an address it would not register, so that a
* credential predating the current rules can still reach the service and be
* answered with the single shared failure message. R13.10 nonetheless requires
* the form to reject an address carrying no `@` separator, so the form applies
* the stricter of the two rules the backend already declares rather than a
* third rule invented here. Suppressing a submission the route would have
* rejected as unknown credentials costs the user nothing.
*/
const loginFormSchema = loginRequestSchema.extend({ email: registrationRequestSchema.shape.email });
/** The registration form's rules, unchanged from the `POST /register` Request_Schema. */
const registrationFormSchema = registrationRequestSchema;
/**
* The password-change form's rules: the `POST /password/change` Request_Schema,
* with the confirmation field the form adds.
*
* The confirmation is judged by the new password's own rule, read off the schema
* rather than restated, so a person cannot confirm a value the route would refuse
* and then be told about it by the route. The equality check reports against the
* confirmation field, so the message sits beside the control that has to change.
*
* The transform drops the confirmation, and it drops it here rather than at the
* component so that the validated value is a `ChangePasswordRequest` exactly: the
* route's schema is a `z.strictObject`, and a body carrying a third property is a
* 400 rather than a change.
*/
const changePasswordFormSchema = changePasswordRequestSchema.extend({ confirmNewPassword: changePasswordRequestSchema.shape.newPassword }).refine((values) => values.confirmNewPassword === values.newPassword, {
	error: "The two new passwords do not match",
	path: ["confirmNewPassword"]
}).transform(({ currentPassword, newPassword }) => ({
	currentPassword,
	newPassword
}));
/**
* The email-change form's rules, unchanged from the `POST /email/change`
* Request_Schema.
*
* The new address is judged by what registration judges an address by, because
* that is the rule the route applies (R8.3): the address asked for here becomes a
* Login_Identifier once proven. Nothing is extended, as `loginFormSchema` extends
* its own, because there is nothing weaker here to strengthen.
*/
const emailChangeFormSchema = requestEmailChangeRequestSchema;
/**
* The reset-request form's rules, unchanged from the `POST /password/reset`
* Request_Schema.
*
* Deliberately **not** strengthened with registration's address format, which is
* what `loginFormSchema` does to its own weaker field. The reasoning that made
* that harmless is what forbids it here: a login suppressed by the form would have
* been answered as unknown credentials anyway, whereas a reset request suppressed
* by the form is a reset the route would have issued a token for. An address
* registered before the current format rule must still be able to reach this
* route, or the person holding one cannot recover their account.
*/
const passwordResetRequestFormSchema = requestPasswordResetRequestSchema;
/**
* The reset-completion form's rules, unchanged from the
* `POST /password/reset/confirm` Request_Schema.
*
* The token is bounded by the same length `VerificationService` refuses a longer
* one by, so a link carrying something that cannot be a token is refused before a
* request is issued. The new password is judged by registration's rules, which is
* what the route applies to a password being stored (R9.8).
*/
const completePasswordResetFormSchema = completePasswordResetRequestSchema;
/**
* The closure form's rules, unchanged from the `POST /account/close`
* Request_Schema.
*
* The bound alone, and no length policy: the stored password may predate the
* current minimum, and a person holding one must still be able to close the
* account. The service answers a mismatch.
*/
const closeAccountFormSchema = closeAccountRequestSchema;
/**
* The key-registration form's rules, unchanged from the
* `POST /webauthn/registration/begin` Request_Schema.
*
* The label alone, bounded exactly as the route bounds it, so the form refuses an
* empty or over-long label before a ceremony begins rather than after the service
* rejects it. The challenge and the response the completion route also carries are not
* entered values and so are not the form's to judge.
*/
const registerKeyFormSchema = beginRegistrationRequestSchema;
/**
* The first-key enrolment form's rules (R8.1): the ticket and the label, picked from the
* `POST /webauthn/enrolment/complete` Request_Schema.
*
* The two entered-or-carried values alone — `pick` drops the challenge and the response
* the completion route also carries, which are a handle the service issued and a response
* the authenticator produced rather than form values. The ticket is bounded by the same
* length a continuation token is, so a mistyped or over-long one is refused before a
* ceremony begins; the label by the same rule key registration applies.
*/
const enrolFirstKeyFormSchema = completeEnrolmentRequestSchema.pick({
	ticket: true,
	label: true
});
/**
* The account-creation form's rules, unchanged from the `POST /admin/accounts`
* Request_Schema.
*
* The address is judged by what registration judges an address by — the route applies
* the registered-email rule to the account it provisions (R5.1) — so a malformed address
* is refused before a request, unlike `loginFormSchema`'s weaker login field.
*/
const createAccountFormSchema = createAccountRequestSchema;
/**
* Validates entered login values.
*
* The password is neither trimmed nor rejected for holding only whitespace,
* because whitespace is significant in a credential and the route does not trim
* it either. Trimming here would deny the login of an account whose password
* the route accepted.
*/
function validateLoginFields(values) {
	return validate(loginFormSchema, values);
}
/** Validates entered registration values. */
function validateRegistrationFields(values) {
	return validate(registrationFormSchema, values);
}
/**
* Validates entered password-change values.
*
* No password is trimmed, for the reason `validateLoginFields` gives: whitespace
* is significant in a credential, and the route does not trim one either.
*/
function validateChangePasswordFields(values) {
	return validate(changePasswordFormSchema, values);
}
/** Validates entered email-change values. */
function validateEmailChangeFields(values) {
	return validate(emailChangeFormSchema, values);
}
/** Validates the address a reset is asked for. */
function validatePasswordResetRequestFields(values) {
	return validate(passwordResetRequestFormSchema, values);
}
/**
* Validates a reset completion: the token the person arrived with, and the
* password being set.
*
* The token is validated beside the password rather than assumed sound, so a link
* carrying nothing usable is refused without a request. A component rendering no
* control for it reports the token's message as a message about the link.
*/
function validateCompletePasswordResetFields(values) {
	return validate(completePasswordResetFormSchema, values);
}
/** Validates the password presented to close an account. */
function validateCloseAccountFields(values) {
	return validate(closeAccountFormSchema, values);
}
/** Validates the label a key registration is given (R1.6). */
function validateRegisterKeyFields(values) {
	return validate(registerKeyFormSchema, values);
}
/**
* Validates a first-key enrolment: the ticket the person carried, and the label for the
* new key (R8.1).
*
* The ticket is validated beside the label rather than assumed sound, so a mistyped or
* over-long ticket is refused without a ceremony. A component rendering the ticket in a
* field reports its message against that field; one given it from the link reports it as
* a message about the link, as the reset completion does.
*/
function validateEnrolFirstKeyFields(values) {
	return validate(enrolFirstKeyFormSchema, values);
}
/** Validates the address a passwordless account is created at (R5.1). */
function validateCreateAccountFields(values) {
	return validate(createAccountFormSchema, values);
}
/**
* The single validation path, mirroring `parseBody` on the backend: one schema,
* one parse, every failing field reported at once rather than the first of them.
*/
function validate(schema, values) {
	const result = schema.safeParse(values);
	if (result.success) return {
		valid: true,
		value: result.data
	};
	return {
		valid: false,
		errors: describeIssues(result.error)
	};
}
/**
* The first issue of each field wins.
*
* A field reports one message, and the checks run in the order the schema
* declares them, so the message names the first rule the value broke — an empty
* address is reported as required rather than as malformed.
*/
function describeIssues(error) {
	const errors = {};
	for (const issue of error.issues) {
		const field = issue.path[0];
		if (typeof field !== "string" || !isKnownField(field)) continue;
		const key = field;
		if (key in errors) continue;
		errors[key] = describeIssue(field, issue);
	}
	return errors;
}
/**
* The label each message opens with.
*
* The keys are {@link KnownField}, the union of every form's fields, so a field
* added to a Request_Schema and rendered by a form fails to compile until it has a
* label. Each label is the visible name of the control the message sits beside, so
* that what a person reads in the message is what they read above the field.
*
* `token` is the one key naming no control. `CompletePasswordReset` accepts a
* token and renders none (R17.11), so its message is shown as a message about the
* link the person followed — which is what the label calls it, because "token" is
* not a word the person was ever shown.
*/
const fieldLabels = {
	email: "Email address",
	password: "Password",
	currentPassword: "Current password",
	newPassword: "New password",
	confirmNewPassword: "Confirm new password",
	newEmail: "New email address",
	token: "Reset link",
	ticket: "Enrolment ticket",
	label: "Key label"
};
function isKnownField(field) {
	return Object.hasOwn(fieldLabels, field);
}
/**
* Writes one issue as a message a user can act on.
*
* Every bound is read from the issue rather than restated, so the message
* follows the Request_Schema without this module knowing what the schema
* permits. An unrecognised issue falls back to Zod's own message, which is
* accurate if less fluent, rather than to a message that might mislead.
*/
function describeIssue(field, issue) {
	const label = fieldLabels[field];
	switch (issue.code) {
		case "invalid_type": return `${label} is required`;
		case "too_small": return Number(issue.minimum) <= 1 ? `${label} is required` : `${label} must be at least ${String(issue.minimum)} characters`;
		case "too_big": return `${label} must be ${String(issue.maximum)} characters or fewer`;
		case "invalid_format": return issue.format === "email" ? `${label} must be a valid email address, including an @ separator` : issue.message;
		default: return issue.message;
	}
}
//#endregion
//#region src/frontend/react/rejection-message.ts
/**
* The message a form shows when the `login` or `register` action rejects
* (R13.5).
*
* Shared by `LoginForm` and `RegisterForm` rather than written twice, because the
* fallback is one decision and a second copy of it would drift. Internal to the
* capability: nothing here reaches the `./frontend` entry point, since a consumer
* displaying the message reads it from the rejection itself.
*/
/**
* What is shown when the rejection carries no message of its own.
*
* Names the failure and the remedy and nothing else. A rejection with an empty
* message is one the platform could not describe — a transport failure, or a
* response whose body carried no `error` — so this message must not claim a cause
* it does not know, and must not name a status code the user cannot act on.
*/
const REQUEST_FAILED_MESSAGE = "The request failed. Please check your connection and try again.";
/**
* Reads the message a rejected action carries, falling back where it carries
* none.
*
* The message the store rejects with is the one R22.8 describes: the `message`
* from the response's `error` body, carried on an `ApplicationError`. This
* function does not narrow to that class, because a form has no business
* insisting on the shape of its failures — a transport error, an
* `ApiRequestError` and a plain `Error` all reach it by the same path, and each of
* them carries a `message` — and a rejection that carries none is the case the
* fallback exists for.
*
* A message of whitespace alone is treated as absent. It renders as a blank
* message, which tells the user less than the fallback does, so emptiness is
* judged by content rather than by length.
*/
function describeRejection(reason) {
	const message = readMessage(reason);
	return message.trim().length > 0 ? message : REQUEST_FAILED_MESSAGE;
}
/**
* The `message` a rejection carries, or the empty string where it carries no
* string one.
*
* `in` reaches the prototype chain, so an `Error` and a plain object holding a
* `message` are read by the same branch. Anything else — a rejected string, a
* `null`, a number — yields the empty string and therefore the fallback, rather
* than a coerced value that would put `[object Object]` or `undefined` on screen.
*/
function readMessage(reason) {
	if (typeof reason === "object" && reason !== null && "message" in reason) return typeof reason.message === "string" ? reason.message : "";
	return "";
}
//#endregion
//#region src/frontend/react/login-form.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$9 = "Sign in";
/**
* A form that signs a user in with an electronic mail address and a password.
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <LoginForm onAuthenticated={() => { navigate('/'); }} />
* </AuthenticationProvider>
* ```
*
* Presentation is entirely Design_System components (R13.2, R13.7): `Card` for
* the surface, `Stack` for the arrangement, `FormField` with `Input` for each
* field, `Alert` for a rejected submission and `Button` for the submit control.
* The form declares no colour, no spacing, no radius and no typography of its own,
* no route and no page layout — where it sits and how wide it is belong to the
* application around it.
*
* `noValidate` is set so that the rules of R13.10 are the only rules that decide a
* submission. The controls still carry `required` and `type="email"`, which is
* what assistive technology reads the field's nature from, but the user agent's
* own interactive validation would otherwise suppress the submission before
* `validateLoginFields` ran and would report the failure in a bubble this form
* neither owns nor can associate with the field.
*/
function LoginForm({ onAuthenticated }) {
	const authentication = useAuthentication();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	/**
	* Whether the submit control is disabled (R13.6).
	*
	* Two conditions, because a submission may be in flight for two reasons. The
	* form's own `submitting` covers the promise this form is awaiting, which is
	* what the criterion speaks of and what stops a second submission being raised
	* from the same form — a disabled default button also suppresses implicit
	* submission by `Enter`, so the guard holds for the keyboard as well as the
	* pointer. The reported `authenticating` status covers an action in flight that
	* this form did not raise, a session recovery among them, and is what a store
	* reporting that status renders as a disabled control with no interaction.
	*
	* Both clear on a rejection: `submitting` in the `finally` below, and the status
	* because the store resets to `anonymous` when `login` rejects. That is R13.5's
	* re-enabling, and it falls out of the state rather than being restored by hand.
	*/
	const pending = submitting || authentication.status === "authenticating";
	async function submit() {
		const outcome = validateLoginFields({
			email,
			password
		});
		setRequestError(null);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.login(outcome.value);
			onAuthenticated?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (pending) return;
		submit();
	}
	function handleEmailChange(event) {
		setEmail(event.target.value);
	}
	function handlePasswordChange(event) {
		setPassword(event.target.value);
	}
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: requestError
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Email address",
				error: errors.email ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "email",
					autoComplete: "email",
					required: true,
					invalid: errors.email !== void 0,
					value: email,
					onChange: handleEmailChange
				})
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Password",
				error: errors.password ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "current-password",
					required: true,
					invalid: errors.password !== void 0,
					value: password,
					onChange: handlePasswordChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled: pending,
				children: SUBMIT_LABEL$9
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/register-form.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$8 = "Create account";
/**
* A form that registers a new account and signs the new user in.
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <RegisterForm onAuthenticated={() => { navigate('/'); }} />
* </AuthenticationProvider>
* ```
*
* The same composition `LoginForm` makes, over the same six Design_System
* components and over the same two fields (R13.2, R13.7, R4.4): an email address
* and a password, which is the whole of what registration accepts. The one
* difference is `autoComplete="new-password"` rather than `current-password`, so
* that a password manager offers to generate a password instead of filling an
* existing one.
*
* The password rules are the registration Request_Schema's rules, applied by
* `validateRegistrationFields`; this file states no bound of its own, and no
* description restates one, so a rule that changes in the schema changes the
* message the field reports without this component being edited.
*/
function RegisterForm({ onAuthenticated }) {
	const authentication = useAuthentication();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	/** Whether the submit control is disabled (R13.6). See `LoginForm`. */
	const pending = submitting || authentication.status === "authenticating";
	async function submit() {
		const outcome = validateRegistrationFields({
			email,
			password
		});
		setRequestError(null);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.register(outcome.value);
			onAuthenticated?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (pending) return;
		submit();
	}
	function handleEmailChange(event) {
		setEmail(event.target.value);
	}
	function handlePasswordChange(event) {
		setPassword(event.target.value);
	}
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: requestError
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Email address",
				error: errors.email ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "email",
					autoComplete: "email",
					required: true,
					invalid: errors.email !== void 0,
					value: email,
					onChange: handleEmailChange
				})
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Password",
				error: errors.password ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "new-password",
					required: true,
					invalid: errors.password !== void 0,
					value: password,
					onChange: handlePasswordChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled: pending,
				children: SUBMIT_LABEL$8
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/user-menu.tsx
/** The text of the control that ends the session. */
const SIGN_OUT_LABEL = "Sign out";
/** The text of the control that asks for the sign-in surface. */
const SIGN_IN_LABEL$1 = "Sign in";
/**
* The identity and the session control of the signed-in user, or a way in for an
* anonymous one.
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <UserMenu onSignInRequested={() => { navigate('/sign-in'); }} />
* </AuthenticationProvider>
* ```
*
* Three rendered states, from the one snapshot `useAuthentication` reports:
*
* | Reported state       | Rendered                                            |
* | -------------------- | --------------------------------------------------- |
* | `authenticated`      | the address or the supplied `name`, and a sign-out control |
* | `unknown`, `authenticating` | nothing                                      |
* | `anonymous`          | a sign-in control, where one is offered             |
*
* The first row is R13.11: the identity the store reported — the account's email
* address, or the `name` the caller supplied in its place — and beside it an enabled
* control that invokes `logout`. Nothing disables that control while the session
* is still reported as authenticated, because the criterion says enabled and means
* it; the guard against a second activation is that the store clears its state in
* a `finally`, so the control unmounts as soon as `logout` settles either way.
*
* The other two rows are R13.12, reached by returning nothing rather than by
* hiding something: while `authenticated` is `false` there is no identity and
* no logout control in the output at all, enabled or otherwise. Nothing is rendered
* during recovery in particular, so a reload shows no sign-in control that a
* recovered session would immediately replace.
*
* Presentation is Design_System components (R13.2, R13.7): `Card` for the surface,
* `Stack` for the arrangement and `Button` for each control. The component declares
* no colour, no spacing, no radius and no typography of its own, no route and no
* page layout — where the menu sits belongs to the application around it.
*/
function UserMenu({ name, onSignInRequested }) {
	const authentication = useAuthentication();
	const user = authentication.user;
	async function signOut() {
		try {
			await authentication.logout();
		} catch {}
	}
	function handleSignOut() {
		signOut();
	}
	if (authentication.authenticated && user !== null) return /* @__PURE__ */ jsxs(Menu, { children: [/* @__PURE__ */ jsx("span", { children: name ?? user.email }), /* @__PURE__ */ jsx(Button, {
		variant: "secondary",
		type: "button",
		onClick: handleSignOut,
		children: SIGN_OUT_LABEL
	})] });
	if (authentication.loading || onSignInRequested === void 0) return null;
	return /* @__PURE__ */ jsx(Menu, { children: /* @__PURE__ */ jsx(Button, {
		type: "button",
		onClick: onSignInRequested,
		children: SIGN_IN_LABEL$1
	}) });
}
/**
* The surface and the arrangement both rendered states share.
*
* Internal, and a component rather than a repeated pair of elements so that the
* two states cannot drift apart into two different surfaces. A horizontal `Stack`
* centred across its cross axis, which is what puts an identity and a control on
* one line with their text aligned, at the small gap the small `Card` padding pairs
* with.
*/
function Menu({ children }) {
	return /* @__PURE__ */ jsx(Card, {
		padding: "small",
		children: /* @__PURE__ */ jsx(Stack, {
			direction: "horizontal",
			gap: "small",
			align: "centre",
			children
		})
	});
}
//#endregion
//#region src/frontend/react/change-password-form.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$7 = "Change password";
/**
* What a completed change reports.
*
* It names the consequence the person cannot see, which is the revocation R7.5
* performs: every other session ends, so the phone in their pocket is signed out
* and they should not be surprised by it. The acting session is spared, which is
* why this form does not report having signed the person out.
*/
const CHANGED_MESSAGE = "Your password has been changed. Every other session has been signed out.";
/**
* A form that replaces the signed-in account's password (R17.1).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <ChangePasswordForm />
* </AuthenticationProvider>
* ```
*
* Three fields, in the order the design's component table gives them: the current
* password, which is the Reauthentication R7.1 requires, the new one, and a
* confirmation. The confirmation is the form's own — no route has a field for it —
* and `changePasswordFormSchema` drops it once the two have been compared, so the
* request carries the two properties the route's `z.strictObject` declares and
* nothing else.
*
* Presentation is entirely Design_System components (R17.2): `Card` for the
* surface, `Stack` for the arrangement, `FormField` with `Input` for each field,
* `Alert` for a rejected or a completed submission and `Button` for the submit
* control. The form declares no colour, no spacing, no radius and no typography of
* its own, no route and no page layout (R17.10) — where it sits belongs to the
* application around it.
*
* Field validation is `validateChangePasswordFields`, which is the route's
* Request_Schema (R17.8). No bound is stated here and no description restates one,
* so a rule that changes in the schema changes what the field reports without this
* component being edited.
*
* `noValidate` is set for the reason `LoginForm` sets it: the user agent's own
* interactive validation would otherwise suppress the submission before the
* schema ran, and would report the failure in a bubble this form neither owns nor
* can associate with the field (R17.9).
*/
function ChangePasswordForm({ onChanged }) {
	const authentication = useAuthentication();
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [changed, setChanged] = useState(false);
	/**
	* Whether the submit control is disabled: one condition, where `LoginForm` has
	* two.
	*
	* The second of `LoginForm`'s is the store's reported `authenticating`, which
	* covers a login, a registration or a session recovery in flight. No account
	* operation reports that status — the store leaves the snapshot alone while one
	* runs, because a password change spares the acting session (R16.3) — so this
	* form's own `submitting` is the whole of the guard. It also suppresses implicit
	* submission by `Enter`, a disabled default control being what the user agent
	* declines to submit through.
	*/
	const pending = submitting;
	async function submit() {
		const outcome = validateChangePasswordFields({
			currentPassword,
			newPassword,
			confirmNewPassword
		});
		setRequestError(null);
		setChanged(false);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.changePassword(outcome.value);
			setCurrentPassword("");
			setNewPassword("");
			setConfirmNewPassword("");
			setChanged(true);
			onChanged?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (pending) return;
		submit();
	}
	function handleCurrentPasswordChange(event) {
		setCurrentPassword(event.target.value);
	}
	function handleNewPasswordChange(event) {
		setNewPassword(event.target.value);
	}
	function handleConfirmNewPasswordChange(event) {
		setConfirmNewPassword(event.target.value);
	}
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: requestError
			}),
			changed ? /* @__PURE__ */ jsx(Alert, {
				tone: "success",
				role: "status",
				children: CHANGED_MESSAGE
			}) : null,
			/* @__PURE__ */ jsx(FormField, {
				label: "Current password",
				error: errors.currentPassword ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "current-password",
					required: true,
					invalid: errors.currentPassword !== void 0,
					value: currentPassword,
					onChange: handleCurrentPasswordChange
				})
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "New password",
				error: errors.newPassword ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "new-password",
					required: true,
					invalid: errors.newPassword !== void 0,
					value: newPassword,
					onChange: handleNewPasswordChange
				})
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Confirm new password",
				error: errors.confirmNewPassword ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "new-password",
					required: true,
					invalid: errors.confirmNewPassword !== void 0,
					value: confirmNewPassword,
					onChange: handleConfirmNewPasswordChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled: pending,
				children: SUBMIT_LABEL$7
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/change-email-form.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$6 = "Request email change";
/**
* What a completed request reports.
*
* It names the address and the act that finishes the change, because the change is
* deliberately not finished: R8.6 leaves the Login_Identifier where it is until the
* token proving the new address is consumed, so a message saying the address had
* changed would be false for as long as the link went unfollowed.
*/
function requestedMessage(newEmail) {
	return `We have sent a link to ${newEmail}. Your account moves to that address when the link is followed.`;
}
/** What a change already awaiting confirmation reports. */
function pendingMessage(newEmail) {
	return `A change to ${newEmail} is waiting to be confirmed. Asking for a different address replaces it.`;
}
/** What the field says about the address the account signs in with today. */
function currentAddressNote(email) {
	return `Your account signs in with ${email} today.`;
}
/**
* A form that asks to move the signed-in account to another email address, and
* shows any change already awaiting confirmation (R17.1).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <ChangeEmailForm />
* </AuthenticationProvider>
* ```
*
* Two fields, in the order the design's component table gives them: the new
* address, and the current password, which is the Reauthentication R8.1 requires.
* Both are judged by `validateEmailChangeFields`, which is the route's
* Request_Schema (R17.8) — the address by the rule registration applies, because
* the address asked for here becomes a Login_Identifier once proven.
*
* The pending change is read on mount, through `getPendingEmailChange`, and held
* here rather than in the store: a pending address is exposed to the account that
* asked for it and to no other (R8.12), so it is no member of `Public_User` and no
* part of the reported snapshot. What is displayed is therefore this component's
* own state, as the entered values are.
*
* Presentation is entirely Design_System components (R17.2), and there is no
* routing, navigation or page layout (R17.10): what follows a request is the
* product's decision, and the link that completes the change is followed in a mail
* client rather than here.
*
* There is no control that withdraws a pending change. R8.13 exposes withdrawal on
* the store and the hook, and a person looking at this form gives up a pending
* address by asking for a different one, which replaces it. A withdraw control is
* additive and would be a component decision rather than a missing one.
*/
function ChangeEmailForm({ onRequested }) {
	const authentication = useAuthentication();
	const [newEmail, setNewEmail] = useState("");
	const [currentPassword, setCurrentPassword] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [pending, setPending] = useState(null);
	const [requested, setRequested] = useState(false);
	const [loadError, setLoadError] = useState(null);
	/**
	* Reads what is already awaiting confirmation, once per mount.
	*
	* The dependency list is empty deliberately, and the captured `authentication` is
	* sound for it: every member of that object delegates to the one store the
	* provider supplied, so a captured `getPendingEmailChange` reads the same store a
	* later render's would. Only the reported snapshot goes stale in a capture, and
	* this effect reads none of it. Depending on the hook's value instead would re-read
	* on every snapshot change — a verification landing, a session ending — which is a
	* request the person did not ask for.
	*
	* The `current` flag is the usual guard: a fixture or a page that unmounts while
	* the read is in flight leaves a resolution with nothing to report to, and the
	* cleanup is what says so.
	*/
	useEffect(() => {
		let current = true;
		async function load() {
			try {
				const answered = await authentication.getPendingEmailChange();
				if (current) setPending(answered);
			} catch (reason) {
				if (current) setLoadError(describeRejection(reason));
			}
		}
		load();
		return () => {
			current = false;
		};
	}, []);
	/** Whether the submit control is disabled. See `ChangePasswordForm`. */
	const submitDisabled = submitting;
	async function submit() {
		const outcome = validateEmailChangeFields({
			newEmail,
			currentPassword
		});
		setRequestError(null);
		setRequested(false);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			const answered = await authentication.requestEmailChange(outcome.value);
			setPending(answered);
			setRequested(true);
			setNewEmail("");
			setCurrentPassword("");
			setLoadError(null);
			onRequested?.(answered);
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (submitDisabled) return;
		submit();
	}
	function handleNewEmailChange(event) {
		setNewEmail(event.target.value);
	}
	function handleCurrentPasswordChange(event) {
		setCurrentPassword(event.target.value);
	}
	const user = authentication.user;
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: requestError
			}),
			loadError === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: loadError
			}),
			requested && pending !== null ? /* @__PURE__ */ jsx(Alert, {
				tone: "success",
				role: "status",
				children: requestedMessage(pending.newEmail)
			}) : null,
			!requested && pending !== null ? /* @__PURE__ */ jsx(Alert, {
				tone: "informational",
				role: "status",
				children: pendingMessage(pending.newEmail)
			}) : null,
			/* @__PURE__ */ jsx(FormField, {
				label: "New email address",
				description: user === null ? "" : currentAddressNote(user.email),
				error: errors.newEmail ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "email",
					autoComplete: "email",
					required: true,
					invalid: errors.newEmail !== void 0,
					value: newEmail,
					onChange: handleNewEmailChange
				})
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Current password",
				error: errors.currentPassword ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "current-password",
					required: true,
					invalid: errors.currentPassword !== void 0,
					value: currentPassword,
					onChange: handleCurrentPasswordChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled: submitDisabled,
				children: SUBMIT_LABEL$6
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/session-list.tsx
/** The text of the control that ends one session. */
const REVOKE_LABEL = "Sign out";
/** The text of the control that opens the confirmation for ending the rest. */
const REVOKE_OTHERS_LABEL = "Sign out every other session";
/** The text of the control that performs it, inside the confirmation. */
const CONFIRM_REVOKE_OTHERS_LABEL = "Sign them out";
/** The text of the control that dismisses the confirmation, changing nothing. */
const CANCEL_LABEL$1 = "Keep them";
/** What the confirmation the destructive act requires is titled (R17.3). */
const CONFIRM_TITLE$1 = "Sign out every other session?";
/**
* What that confirmation describes.
*
* It names what survives as well as what ends, because that is what the person is
* deciding: R11.2 spares the session the request is made from, so they stay signed
* in here and every other device is signed out.
*/
const CONFIRM_DESCRIPTION$1 = "Every device other than this one will be signed out. This session stays signed in, and nothing can undo it.";
/** What is shown while the first read is in flight. */
const LOADING_MESSAGE$2 = "Reading the sessions on this account…";
/**
* What is shown where the account holds none.
*
* Reachable rather than theoretical: a revoked or expired session is left out of
* the list (R10.7), so an account read from a token whose own session has since
* expired is answered with nothing.
*/
const NO_SESSIONS_MESSAGE = "This account holds no active session.";
/** How the Current_Session is marked (R10.4). */
const CURRENT_MARKER = "This session";
/** What is said of a session started by something with no user agent to give. */
const UNKNOWN_DEVICE = "An unknown device";
/** What is said of a session started from no known network address. */
const UNKNOWN_PLACE = "an unknown address";
/** What one completed revocation reports. */
function revokedMessage(session) {
	return session.current ? "This session has been signed out." : `${describeSession(session)} has been signed out.`;
}
/**
* What a completed revoke-others reports.
*
* The count is the one the route answered with, so a person who has just signed out
* four devices is told it was four rather than left to count the rows that went.
*/
function revokedOthersMessage(count) {
	return count === 1 ? "One other session has been signed out." : `${String(count)} other sessions have been signed out.`;
}
/**
* How a session is named in a message and in a control's accessible name.
*
* The Session_Metadata and nothing else, because that is what the list exists for:
* a person reads it to recognise a session that is not theirs (R10.4). Either half
* may be absent — a session started by a caller outside HTTP has neither — and the
* absence is said rather than left blank.
*/
function describeSession(session) {
	return `${session.userAgent ?? UNKNOWN_DEVICE} from ${session.createdFromAddress ?? UNKNOWN_PLACE}`;
}
/**
* The sessions an account holds, with a control that ends one and a control that
* ends every other (R17.1).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <SessionList />
* </AuthenticationProvider>
* ```
*
* The list is read once per mount and held here. It is deliberately not re-read
* afterwards: R16.6 makes a listing a point-in-time answer to a question the person
* asked, and re-reading after they revoke the session they are reading from would
* ask with a credential the act has just destroyed. A revocation therefore drops the
* rows it ended from what is held, which is what actually happened rather than a
* guess about it.
*
* Ending every other session is destructive and irreversible, so it is confirmed
* before it happens (R17.3), through the Design_System's `Dialog`. The focus trap,
* the return of focus to the control that opened it and the dismissal on escape are
* the dialog's own; nothing here reimplements any of them. Ending **one** session is
* not put behind a confirmation: the person has just read the row they are acting
* on, and R17.3 names the two acts that need one.
*
* Presentation is entirely Design_System components (R17.2), and there is no
* routing, navigation or page layout (R17.10). No descriptor carries a refresh
* credential, a Token_Digest or an access token (R10.5), so there is nothing on the
* rows this component has to omit.
*/
function SessionList({ onRevoked }) {
	const authentication = useAuthentication();
	const [sessions, setSessions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [revoking, setRevoking] = useState(null);
	const [revokingOthers, setRevokingOthers] = useState(false);
	const [actionError, setActionError] = useState(null);
	const [report, setReport] = useState(null);
	const [confirming, setConfirming] = useState(false);
	/**
	* Reads the list, once per mount.
	*
	* The empty dependency list and the captured `authentication` are sound for the
	* reason `ChangeEmailForm`'s effect records: every member of the hook's value
	* delegates to the one store, and this effect reads no part of the snapshot.
	* Depending on the hook's value instead would re-read whenever the reported state
	* moved — which revoking the Current_Session does, with a credential that no
	* longer exists.
	*/
	useEffect(() => {
		let current = true;
		async function load() {
			try {
				const answered = await authentication.listSessions();
				if (current) setSessions(answered);
			} catch (reason) {
				if (current) setLoadError(describeRejection(reason));
			} finally {
				if (current) setLoading(false);
			}
		}
		load();
		return () => {
			current = false;
		};
	}, []);
	/** Whether any revocation is in flight, and therefore every control disabled. */
	const pending = revoking !== null || revokingOthers;
	async function revoke(session) {
		setActionError(null);
		setReport(null);
		setRevoking(session.id);
		try {
			await authentication.revokeSession(session.id);
			setSessions((held) => held.filter((each) => each.id !== session.id));
			setReport(revokedMessage(session));
			onRevoked?.();
		} catch (reason) {
			setActionError(describeRejection(reason));
		} finally {
			setRevoking(null);
		}
	}
	async function revokeOthers() {
		setActionError(null);
		setReport(null);
		setRevokingOthers(true);
		try {
			const count = await authentication.revokeOtherSessions();
			setSessions((held) => held.filter((each) => each.current));
			setReport(revokedOthersMessage(count));
			setConfirming(false);
			onRevoked?.();
		} catch (reason) {
			setActionError(describeRejection(reason));
		} finally {
			setRevokingOthers(false);
		}
	}
	function handleRevoke(session) {
		return () => {
			if (pending) return;
			revoke(session);
		};
	}
	function handleRevokeOthers() {
		if (pending) return;
		revokeOthers();
	}
	function handleCancel() {
		setConfirming(false);
	}
	/** Whether there is anything for the revoke-others control to end. */
	const others = sessions.some((session) => !session.current);
	return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
		actionError === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "destructive",
			children: actionError
		}),
		loadError === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "destructive",
			children: loadError
		}),
		report === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "success",
			role: "status",
			children: report
		}),
		loading ? /* @__PURE__ */ jsx(Alert, {
			tone: "informational",
			role: "status",
			children: LOADING_MESSAGE$2
		}) : null,
		!loading && loadError === null && sessions.length === 0 ? /* @__PURE__ */ jsx(Alert, {
			tone: "informational",
			role: "status",
			children: NO_SESSIONS_MESSAGE
		}) : null,
		sessions.map((session) => /* @__PURE__ */ jsx(SessionRow, {
			session,
			disabled: pending,
			onRevoke: handleRevoke(session)
		}, session.id)),
		others ? /* @__PURE__ */ jsxs(Dialog, {
			open: confirming,
			onOpenChange: setConfirming,
			children: [/* @__PURE__ */ jsx(DialogTrigger, {
				asChild: true,
				children: /* @__PURE__ */ jsx(Button, {
					variant: "destructive",
					type: "button",
					disabled: pending,
					children: REVOKE_OTHERS_LABEL
				})
			}), /* @__PURE__ */ jsxs(DialogContent, { children: [/* @__PURE__ */ jsxs(DialogHeader, { children: [/* @__PURE__ */ jsx(DialogTitle, { children: CONFIRM_TITLE$1 }), /* @__PURE__ */ jsx(DialogDescription, { children: CONFIRM_DESCRIPTION$1 })] }), /* @__PURE__ */ jsxs(DialogFooter, { children: [/* @__PURE__ */ jsx(Button, {
				variant: "secondary",
				type: "button",
				disabled: pending,
				onClick: handleCancel,
				children: CANCEL_LABEL$1
			}), /* @__PURE__ */ jsx(Button, {
				variant: "destructive",
				type: "button",
				disabled: pending,
				onClick: handleRevokeOthers,
				children: CONFIRM_REVOKE_OTHERS_LABEL
			})] })] })]
		}) : null
	] }) });
}
/**
* One session: what it describes, and the control that ends it.
*
* Internal, and a component rather than a repeated block so that the rows cannot
* drift apart. A `Card` at the small padding step inside the list's own `Card`,
* which is how a row is told apart from the surface it sits on without this
* component declaring a colour or a border of its own (R17.2).
*
* The device and address lines are plain `span`s, as `UserMenu`'s identity line is
* and for the same reason: neither is an interactive control, a label, a field
* message or a request error, which is what the Design_System's Primitives cover,
* and it exports no text Primitive for either. The three instant lines are a `span`
* of label text beside an `InstantDisplay`, which is the Design_System Primitive that
* renders an instant: it emits a `time` element carrying the machine-readable value,
* formats in the reader's own locale and time zone, and shows an unparseable value as
* it arrived rather than as `Invalid Date` — the fallback this component held before
* that Primitive existed and which now lives there. Each line takes the font, the
* size and the colour of the `Card` it sits in, so nothing is declared here.
*
* The rows are not a `ul`. A list element carries the user agent's own markers and
* indentation, and removing them would mean declaring visual values this component
* may not declare; the Design_System publishes no list Primitive that would. What
* the semantics would have bought — telling one row from another — is bought instead
* by each control's accessible name, which is the session it acts on.
*/
function SessionRow({ session, disabled, onRevoke }) {
	/**
	* What the control's accessible name says the act applies to.
	*
	* The Current_Session is named as the person sees it marked rather than by its
	* metadata, because two sessions started from one browser carry identical
	* metadata and "sign out this session" is the one description that cannot be
	* confused with another row.
	*/
	const subject = session.current ? CURRENT_MARKER.toLowerCase() : describeSession(session);
	return /* @__PURE__ */ jsx(Card, {
		padding: "small",
		children: /* @__PURE__ */ jsxs(Stack, {
			direction: "horizontal",
			gap: "small",
			align: "centre",
			children: [/* @__PURE__ */ jsxs(Stack, {
				gap: "none",
				children: [
					/* @__PURE__ */ jsx("span", { children: session.userAgent ?? UNKNOWN_DEVICE }),
					/* @__PURE__ */ jsx("span", { children: session.createdFromAddress ?? UNKNOWN_PLACE }),
					/* @__PURE__ */ jsxs("span", { children: ["Started ", /* @__PURE__ */ jsx(InstantDisplay, { instant: session.createdAt })] }),
					/* @__PURE__ */ jsxs("span", { children: ["Last refreshed ", /* @__PURE__ */ jsx(InstantDisplay, { instant: session.lastRotatedAt })] }),
					/* @__PURE__ */ jsxs("span", { children: ["Ends ", /* @__PURE__ */ jsx(InstantDisplay, { instant: session.expiresAt })] }),
					session.current ? /* @__PURE__ */ jsx("span", { children: CURRENT_MARKER }) : null
				]
			}), /* @__PURE__ */ jsx(Button, {
				variant: "destructive",
				type: "button",
				disabled,
				"aria-label": `${REVOKE_LABEL} ${subject}`,
				onClick: onRevoke,
				children: REVOKE_LABEL
			})]
		})
	});
}
//#endregion
//#region src/frontend/react/request-password-reset.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$5 = "Send a reset link";
/**
* The one message every outcome renders, which is R9.2 carried into the interface.
*
* The route answers a registered address and an unregistered one identically, and
* this is what that indistinguishability looks like on screen: the conditional
* "if", and no claim about whether anything was sent. A component that said "check
* your inbox" for a registered address and "no such account" otherwise would undo
* the property the route was written to have, and would do it in the one place a
* person can see.
*
* It is rendered for a rejection too. The Throttle_Interval is refused for a
* registered address and answered for an unregistered one (R6.5), so a rejection is
* itself a signal about the address — which is why this form has no rejection
* message of its own and holds no rejection to render.
*/
const ASKED_MESSAGE = "If that address belongs to an account, a link for setting a new password is on its way to it.";
/**
* A form that asks for a link replacing a forgotten password (R17.1).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <RequestPasswordReset />
* </AuthenticationProvider>
* ```
*
* Unauthenticated: a person who cannot remember their password cannot be signed in,
* and the store exposes the operation without a caller having to know that (R16.11).
* One field, judged by `validatePasswordResetRequestFields`, which is the route's
* Request_Schema (R17.8) — bounded and trimmed, with **no** address format rule,
* because an address registered before the current rule must still be able to ask
* for a reset. `validation.ts` records why this is the one form that does not borrow
* registration's stricter rule the way `LoginForm` does.
*
* A field failure is still reported against the field. It is not an outcome of the
* request — no request was issued — so reporting it says nothing about which
* addresses are registered, and R17.9 requires the message to be there.
*
* Presentation is entirely Design_System components (R17.2), and there is no
* routing, navigation or page layout (R17.10): the link is followed from an inbox,
* and where that lands is the product's decision.
*/
function RequestPasswordReset({ onAsked }) {
	const authentication = useAuthentication();
	const [email, setEmail] = useState("");
	const [errors, setErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	/**
	* Whether the request has settled: the submitted state and the rejected state,
	* which are one state here.
	*
	* There is no second flag and no held message. Two states a person could tell
	* apart would be two answers, and the route has one.
	*/
	const [asked, setAsked] = useState(false);
	/** Whether the submit control is disabled. See `ChangePasswordForm`. */
	const pending = submitting;
	async function submit() {
		const outcome = validatePasswordResetRequestFields({ email });
		if (!outcome.valid) {
			setAsked(false);
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.requestPasswordReset(outcome.value.email);
		} catch {} finally {
			setSubmitting(false);
		}
		setAsked(true);
		onAsked?.();
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (pending) return;
		submit();
	}
	function handleEmailChange(event) {
		setEmail(event.target.value);
	}
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			asked ? /* @__PURE__ */ jsx(Alert, {
				tone: "informational",
				role: "status",
				children: ASKED_MESSAGE
			}) : null,
			/* @__PURE__ */ jsx(FormField, {
				label: "Email address",
				error: errors.email ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "email",
					autoComplete: "email",
					required: true,
					invalid: errors.email !== void 0,
					value: email,
					onChange: handleEmailChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled: pending,
				children: SUBMIT_LABEL$5
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/complete-password-reset.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$4 = "Set new password";
/**
* What a completed reset reports.
*
* It names both consequences the person cannot see: every session ended, this one
* included (R9.9), and no session was started in their place (R9.10). A message
* that only said the password had changed would leave them looking for the signed-in
* state a reset deliberately does not grant.
*/
const COMPLETED_MESSAGE = "Your password has been set. Every session on the account has been signed out, so sign in with the new password.";
/**
* A form that sets a new password with the token from a reset link (R17.1).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <CompletePasswordReset token={tokenFromTheLink} />
* </AuthenticationProvider>
* ```
*
* Unauthenticated: the token is the credential, and a person who followed a link
* from an inbox holds no session. One rendered field — the new password — judged by
* `validateCompletePasswordResetFields`, which is the route's Request_Schema
* (R17.8), so the password is judged by registration's own rules (R9.8).
*
* The token is judged by that schema too, against the length
* `VerificationService` refuses a longer one by, and a token failure is reported as
* a message about the link rather than against a field: there is no control for it,
* because R17.11 permits accepting a token and not displaying one. Both that and a
* rejected submission are shown in the same `Alert`, since neither is something the
* person can correct in a field.
*
* Presentation is entirely Design_System components (R17.2), and there is no
* routing, navigation or page layout (R17.10).
*/
function CompletePasswordReset({ token, onCompleted }) {
	const authentication = useAuthentication();
	const [newPassword, setNewPassword] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [completed, setCompleted] = useState(false);
	/**
	* Whether the submit control is disabled.
	*
	* Two conditions, and the second is not `LoginForm`'s reported status but this
	* flow's own arithmetic: a token authorises at most one act, so once the reset has
	* completed there is nothing left for the control to do. Submitting again would be
	* answered with the one indistinguishable token rejection, which would read as
	* though the password had not been set.
	*/
	const disabled = submitting || completed;
	async function submit() {
		const outcome = validateCompletePasswordResetFields({
			token,
			newPassword
		});
		setRequestError(null);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.completePasswordReset(outcome.value.token, outcome.value.newPassword);
			setNewPassword("");
			setCompleted(true);
			onCompleted?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (disabled) return;
		submit();
	}
	function handleNewPasswordChange(event) {
		setNewPassword(event.target.value);
	}
	/**
	* The one failure this form reports outside a field: a link that cannot be used,
	* or a submission the route refused.
	*
	* One element for both, because neither is something a control can be pointed at.
	* The link is judged first and locally, so a token that could not be a token never
	* reaches the route.
	*/
	const failure = requestError ?? errors.token ?? null;
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			failure === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: failure
			}),
			completed ? /* @__PURE__ */ jsx(Alert, {
				tone: "success",
				role: "status",
				children: COMPLETED_MESSAGE
			}) : null,
			/* @__PURE__ */ jsx(FormField, {
				label: "New password",
				error: errors.newPassword ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "new-password",
					required: true,
					invalid: errors.newPassword !== void 0,
					value: newPassword,
					onChange: handleNewPasswordChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled,
				children: SUBMIT_LABEL$4
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/verify-email-prompt.tsx
/** The text of the control that asks for another link. */
const RESEND_LABEL = "Send another link";
/**
* What the standing prompt says.
*
* It names the consequence rather than only the state, because "not verified" alone
* does not tell a person why they should act.
*/
const UNVERIFIED_MESSAGE = "Your email address has not been confirmed yet. Follow the link we sent to it, or ask for another.";
/** What a completed resend reports. */
function resentMessage(email) {
	return `Another link is on its way to ${email}.`;
}
/**
* A prompt shown to an account whose email address is not yet proven, with a
* control that asks for another link (R17.1).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <VerifyEmailPrompt />
* </AuthenticationProvider>
* ```
*
* Renders nothing at all where there is nothing to prompt: no reported user, or one
* whose address is verified. Nothing is returned rather than something hidden, as
* `UserMenu` returns nothing for its own two states, so a page composing this
* unconditionally shows a prompt exactly while one is warranted — and it disappears
* on its own when the address is proven, because `verifyEmail` and
* `confirmEmailChange` adopt the user the route answered with and the reported
* `emailVerified` moves with it (R16.2).
*
* There is no control here that consumes a token, and no field for one. Proving an
* address is `verifyEmail` on the store, called from wherever the link landed; this
* component is the prompt R17.1 asks for and the resend R6.1 exposes, and neither
* reads a token (R17.11).
*
* A refused resend is shown as the message the route raised. Two are expected and
* both are worth reading: an address that has been verified since the snapshot was
* taken is a `ConflictError` (R6.2), and a second request inside the
* Throttle_Interval is refused (R6.3). Unlike a reset request, neither discloses
* anything the caller does not already know — the account is the one its own access
* token names — so there is no reason to render one message for both.
*
* Presentation is entirely Design_System components (R17.2), and there is no
* routing, navigation or page layout (R17.10).
*/
function VerifyEmailPrompt({ onResent }) {
	const authentication = useAuthentication();
	const [submitting, setSubmitting] = useState(false);
	const [requestError, setRequestError] = useState(null);
	const [resent, setResent] = useState(false);
	const user = authentication.user;
	async function resend() {
		setRequestError(null);
		setResent(false);
		setSubmitting(true);
		try {
			await authentication.resendVerification();
			setResent(true);
			onResent?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleResend() {
		if (submitting) return;
		resend();
	}
	if (user === null || user.emailVerified) return null;
	return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
		requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "destructive",
			children: requestError
		}),
		/* @__PURE__ */ jsx(Alert, {
			tone: "warning",
			role: "status",
			children: UNVERIFIED_MESSAGE
		}),
		resent ? /* @__PURE__ */ jsx(Alert, {
			tone: "success",
			role: "status",
			children: resentMessage(user.email)
		}) : null,
		/* @__PURE__ */ jsx(Button, {
			type: "button",
			disabled: submitting,
			onClick: handleResend,
			children: RESEND_LABEL
		})
	] }) });
}
//#endregion
//#region src/frontend/react/close-account-form.tsx
/** The text of the control that asks for the confirmation. */
const SUBMIT_LABEL$3 = "Close account";
/** The text of the control that performs the closure, inside the confirmation. */
const CONFIRM_LABEL = "Close it";
/** The text of the control that dismisses the confirmation, changing nothing. */
const CANCEL_LABEL = "Keep it";
/** What the confirmation the destructive act requires is titled (R17.3). */
const CONFIRM_TITLE = "Close this account?";
/**
* What that confirmation describes.
*
* The three consequences a person cannot see, each of which R12 requires: every
* session ends (R12.3), the address stays taken so nobody else may register it
* (R12.6), and there is no reopening (R12.8). Saying "this cannot be undone" without
* saying what it does would be a warning about nothing in particular.
*/
const CONFIRM_DESCRIPTION = "Every session ends immediately, the email address stays reserved so no new account can use it, and the account cannot be reopened.";
/** What a completed closure reports. */
const CLOSED_MESSAGE = "This account is closed. You have been signed out of every session.";
/**
* A form that closes the signed-in account, behind a confirmation (R17.1, R17.3).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <CloseAccountForm />
* </AuthenticationProvider>
* ```
*
* One field, the current password, which is the Reauthentication R12.1 requires,
* judged by `validateCloseAccountFields` — the route's Request_Schema (R17.8),
* which bounds the value and applies no length policy to it, because the stored
* password may predate the current minimum and a person holding one must still be
* able to close the account.
*
* Closure is destructive and irreversible, so nothing is submitted until the person
* has confirmed it (R17.3), through the Design_System's `Dialog`. The focus trap
* while it is open, the return of focus when it closes and the dismissal on escape
* are the dialog's own.
*
* The dialog is opened by this form rather than by a `DialogTrigger`, and the reason
* is the order of the two checks. A `DialogTrigger` renders `type="button"`, so it
* would never submit the form, and the confirmation would then stand over a password
* nothing had judged — a person would confirm an irreversible act and be told
* afterwards that they had left the field empty. Submitting validates first and opens
* the confirmation only for a value the route would accept. Focus still returns
* where it should: the control that opened the dialog is the control that had focus,
* which is what the primitive restores to.
*
* Presentation is entirely Design_System components (R17.2), and there is no
* routing, navigation or page layout (R17.10): the component reports the closure and
* stops.
*/
function CloseAccountForm({ onClosed }) {
	const authentication = useAuthentication();
	const [currentPassword, setCurrentPassword] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [closed, setClosed] = useState(false);
	const [confirming, setConfirming] = useState(false);
	/**
	* Whether the controls are disabled.
	*
	* `closed` is among the conditions because there is nothing left to close: a second
	* closure is a `ConflictError` (R12.9), and offering a control that can only report
	* one would be offering to do something that has already happened.
	*/
	const disabled = submitting || closed;
	/**
	* Performs the closure, once the person has confirmed it.
	*
	* The entered value is validated again here rather than carried over from the
	* submission that opened the confirmation. The rule is then applied in one place
	* and at the moment of the act, and no second piece of state holds a password.
	*/
	async function close() {
		const outcome = validateCloseAccountFields({ currentPassword });
		setRequestError(null);
		if (!outcome.valid) {
			setConfirming(false);
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.closeAccount(outcome.value.currentPassword);
			setCurrentPassword("");
			setClosed(true);
			setConfirming(false);
			onClosed?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function submit() {
		setRequestError(null);
		const outcome = validateCloseAccountFields({ currentPassword });
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setConfirming(true);
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (disabled) return;
		submit();
	}
	function handleConfirm() {
		if (disabled) return;
		close();
	}
	function handleCancel() {
		setConfirming(false);
	}
	function handleCurrentPasswordChange(event) {
		setCurrentPassword(event.target.value);
	}
	return /* @__PURE__ */ jsxs("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: [/* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			requestError === null || confirming ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: requestError
			}),
			closed ? /* @__PURE__ */ jsx(Alert, {
				tone: "success",
				role: "status",
				children: CLOSED_MESSAGE
			}) : null,
			/* @__PURE__ */ jsx(FormField, {
				label: "Current password",
				error: errors.currentPassword ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "password",
					autoComplete: "current-password",
					required: true,
					invalid: errors.currentPassword !== void 0,
					value: currentPassword,
					onChange: handleCurrentPasswordChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				variant: "destructive",
				type: "submit",
				disabled,
				children: SUBMIT_LABEL$3
			})
		] }) }), /* @__PURE__ */ jsx(Dialog, {
			open: confirming,
			onOpenChange: setConfirming,
			children: /* @__PURE__ */ jsxs(DialogContent, { children: [
				/* @__PURE__ */ jsxs(DialogHeader, { children: [/* @__PURE__ */ jsx(DialogTitle, { children: CONFIRM_TITLE }), /* @__PURE__ */ jsx(DialogDescription, { children: CONFIRM_DESCRIPTION })] }),
				requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
					tone: "destructive",
					children: requestError
				}),
				/* @__PURE__ */ jsxs(DialogFooter, { children: [/* @__PURE__ */ jsx(Button, {
					variant: "secondary",
					type: "button",
					disabled: submitting,
					onClick: handleCancel,
					children: CANCEL_LABEL
				}), /* @__PURE__ */ jsx(Button, {
					variant: "destructive",
					type: "button",
					disabled,
					onClick: handleConfirm,
					children: CONFIRM_LABEL
				})] })
			] })
		})]
	});
}
//#endregion
//#region src/frontend/react/passkey-login-button.tsx
/** The text of the control, in both its enabled and its disabled state. */
const SIGN_IN_LABEL = "Sign in with a security key";
/**
* The passwordless login: a single control that drives the WebAuthn assertion ceremony
* and reports a refusal (R2.2, R9.3).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <PasskeyLoginButton onAuthenticated={() => { navigate('/'); }} />
* </AuthenticationProvider>
* ```
*
* The key is the whole proof — no address, no password, no field at all — so this is a
* button and a message rather than a form: the authenticator chooses the discoverable
* credential, so there is nothing for a person to type. Pressing it begins the ceremony
* (`beginWebAuthnAssertion`), hands the server's options to the authenticator, and
* completes it (`completeWebAuthnAssertion`); a verifying assertion issues the ordinary
* session and the callback fires.
*
* Presentation is entirely Design_System components (R13.2, R13.7): `Stack` for the
* arrangement, `Button` for the control and `Alert` for a refusal. It declares no
* colour, spacing, radius or typography of its own, and no route or page layout —
* where it sits is the application's. The one thing it does not render is a success
* state: a verified assertion signs the person in, and what follows that is the
* product's decision through `onAuthenticated` (R17.10).
*/
function PasskeyLoginButton({ onAuthenticated }) {
	const authentication = useAuthentication();
	const [submitting, setSubmitting] = useState(false);
	const [requestError, setRequestError] = useState(null);
	/**
	* Whether the control is disabled (R13.6).
	*
	* The form's own `submitting` covers the ceremony this component is awaiting, and the
	* reported `authenticating` status covers one in flight it did not raise — a session
	* recovery among them. Both clear on a rejection: `submitting` in the `finally`, and
	* the status because the store resets to `anonymous` when the ceremony rejects. That
	* is R13.5's re-enabling, falling out of the state rather than restored by hand.
	*/
	const pending = submitting || authentication.status === "authenticating";
	async function authenticate() {
		setRequestError(null);
		setSubmitting(true);
		try {
			await authentication.authenticateWithPasskey();
			onAuthenticated?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleClick() {
		if (pending) return;
		authenticate();
	}
	return /* @__PURE__ */ jsxs(Stack, { children: [requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
		tone: "destructive",
		children: requestError
	}), /* @__PURE__ */ jsx(Button, {
		type: "button",
		disabled: pending,
		onClick: handleClick,
		children: SIGN_IN_LABEL
	})] });
}
//#endregion
//#region src/frontend/react/register-key-button.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$2 = "Register a security key";
/**
* Registers a passkey against the signed-in account — a self-registration ceremony with
* a label (R1.6).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <RegisterKeyButton onRegistered={() => reload()} />
* </AuthenticationProvider>
* ```
*
* The account is the one the session names, so the only thing a person enters is a
* label to tell this key from another on the account. Submitting begins the ceremony
* (`beginWebAuthnRegistration`) with the label, drives the authenticator, and completes
* it (`completeWebAuthnRegistration`); the session is untouched, a key being registered
* rather than a session begun.
*
* Presentation is entirely Design_System components (R13.2, R13.7): `Card` the surface,
* `Stack` the arrangement, `FormField` with `Input` the label field, `Alert` a refusal
* and `Button` the submit control. `noValidate` is set so the only rules deciding a
* submission are R13.10's.
*/
function RegisterKeyButton({ onRegistered }) {
	const authentication = useAuthentication();
	const [label, setLabel] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const pending = submitting || authentication.status === "authenticating";
	async function submit() {
		const outcome = validateRegisterKeyFields({ label });
		setRequestError(null);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.registerPasskey(outcome.value.label);
			setLabel("");
			onRegistered?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (pending) return;
		submit();
	}
	function handleLabelChange(event) {
		setLabel(event.target.value);
	}
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: requestError
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Key label",
				error: errors.label ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "text",
					autoComplete: "off",
					required: true,
					invalid: errors.label !== void 0,
					value: label,
					onChange: handleLabelChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled: pending,
				children: SUBMIT_LABEL$2
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/enrol-first-key-form.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL$1 = "Enrol this key";
/**
* What a completed enrolment reports.
*
* It names the consequence the person cannot see: a key is enrolled but no session was
* started (R8.1), so they must now sign in. A message that only said the key was enrolled
* would leave them waiting for a signed-in state the enrolment deliberately does not
* grant.
*/
const ENROLLED_MESSAGE = "Your key is enrolled. No session was started, so sign in with the key you just registered.";
/**
* A form that enrols a bootstrap account's first key with a one-time ticket (R8.1).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <EnrolFirstKeyForm ticket={ticketFromTheLink} />
* </AuthenticationProvider>
* ```
*
* Unauthenticated: the ticket is the credential, and a bootstrap account has no session.
* Two fields — the ticket (which a product may pre-fill from the enrol link) and the
* key's label — judged by `validateEnrolFirstKeyFields`, which is the route's
* Request_Schema (R17.8), so the ticket is bounded exactly as the route bounds it and a
* mistyped one is refused before a ceremony begins.
*
* A ticket rejection from the route is reported as a message about the ticket rather than
* against a field, in the same `Alert` as a validation failure, since every ticket
* rejection reads alike (invariant 15) and neither is something a control can correct
* beyond re-entering the ticket. Presentation is entirely Design_System components
* (R17.2), and there is no routing, navigation or page layout (R17.10).
*/
function EnrolFirstKeyForm({ ticket = "", onEnrolled }) {
	const authentication = useAuthentication();
	const [ticketValue, setTicketValue] = useState(ticket);
	const [label, setLabel] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [enrolled, setEnrolled] = useState(false);
	const disabled = submitting || enrolled;
	async function submit() {
		const outcome = validateEnrolFirstKeyFields({
			ticket: ticketValue,
			label
		});
		setRequestError(null);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			await authentication.enrolFirstKey(outcome.value.ticket, outcome.value.label);
			setEnrolled(true);
			onEnrolled?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (disabled) return;
		submit();
	}
	function handleTicketChange(event) {
		setTicketValue(event.target.value);
	}
	function handleLabelChange(event) {
		setLabel(event.target.value);
	}
	const ticketFailure = requestError ?? errors.ticket ?? null;
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			ticketFailure === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: ticketFailure
			}),
			enrolled ? /* @__PURE__ */ jsx(Alert, {
				tone: "success",
				role: "status",
				children: ENROLLED_MESSAGE
			}) : null,
			/* @__PURE__ */ jsx(FormField, {
				label: "Enrolment ticket",
				error: errors.ticket ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					required: true,
					invalid: errors.ticket !== void 0,
					value: ticketValue,
					onChange: handleTicketChange
				})
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Key label",
				error: errors.label ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					required: true,
					invalid: errors.label !== void 0,
					value: label,
					onChange: handleLabelChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled,
				children: SUBMIT_LABEL$1
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/credential-list.tsx
/** The text of the control that disables one key. */
const DISABLE_LABEL$1 = "Disable";
/** What is shown while the first read is in flight. */
const LOADING_MESSAGE$1 = "Reading the keys on this account…";
/** What is shown where the account holds no key — reachable once the last is disabled. */
const NO_CREDENTIALS_MESSAGE = "This account holds no security key.";
/** What the table is captioned. */
const TABLE_CAPTION$1 = "The security keys registered on this account.";
/** How a key with no attested model is described in place of a model name. */
const UNKNOWN_MODEL = "Unknown model";
/** How a key the attestation reported no last-use for is described. */
const NEVER_USED = "Never used";
/** What one completed disable reports. */
function disabledMessage$1(credential) {
	return `The key “${credential.label}” has been disabled.`;
}
/**
* The security keys an account holds, with a control that disables one (R7.3, R7.5).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <CredentialList userId={accountId} />
* </AuthenticationProvider>
* ```
*
* The list is read once per mount and held here, as a session listing is (R16.6):
* re-reading it is a question a person did not ask. Disabling a key is **terminal**
* (R7.5) and is permitted even where it leaves a passwordless account with no usable key
* (R7.4) — recovery is a fresh registration — so a disabled key is dropped from what is
* held rather than shown greyed, and a key the listing already reported disabled renders
* with no control.
*
* Presentation is entirely Design_System components (R17.2): a `Table` of the keys —
* the key material-free `CredentialDescriptor` carries nothing to omit — with
* `InstantDisplay` for each instant, a `Badge` marking a disabled key, and `Button` the
* disable control. It declares no visual value of its own and no route or page layout.
*/
function CredentialList({ userId, onDisabled }) {
	const authentication = useAuthentication();
	const [credentials, setCredentials] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [disabling, setDisabling] = useState(null);
	const [actionError, setActionError] = useState(null);
	const [report, setReport] = useState(null);
	/**
	* Reads the list, once per mount, re-reading on a change of target.
	*
	* `userId` is a dependency because it names which account's keys to read: an
	* administrative page moving from one account to another must read the second's.
	* `authentication` is captured and not depended upon, as `SessionList`'s effect
	* records — every member delegates to the one store, and depending on the hook's value
	* would re-read whenever the reported state moved.
	*/
	useEffect(() => {
		let current = true;
		async function load() {
			try {
				const answered = await authentication.listCredentials(userId);
				if (current) setCredentials(answered);
			} catch (reason) {
				if (current) setLoadError(describeRejection(reason));
			} finally {
				if (current) setLoading(false);
			}
		}
		load();
		return () => {
			current = false;
		};
	}, [userId]);
	/** Whether a disable is in flight, and therefore every control disabled. */
	const pending = disabling !== null;
	async function disable(credential) {
		setActionError(null);
		setReport(null);
		setDisabling(credential.id);
		try {
			await authentication.disableCredential(credential.id);
			setCredentials((held) => held.filter((each) => each.id !== credential.id));
			setReport(disabledMessage$1(credential));
			onDisabled?.();
		} catch (reason) {
			setActionError(describeRejection(reason));
		} finally {
			setDisabling(null);
		}
	}
	function handleDisable(credential) {
		return () => {
			if (pending) return;
			disable(credential);
		};
	}
	return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
		actionError === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "destructive",
			children: actionError
		}),
		loadError === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "destructive",
			children: loadError
		}),
		report === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "success",
			role: "status",
			children: report
		}),
		loading ? /* @__PURE__ */ jsx(Alert, {
			tone: "informational",
			role: "status",
			children: LOADING_MESSAGE$1
		}) : null,
		!loading && loadError === null && credentials.length === 0 ? /* @__PURE__ */ jsx(Alert, {
			tone: "informational",
			role: "status",
			children: NO_CREDENTIALS_MESSAGE
		}) : null,
		credentials.length > 0 ? /* @__PURE__ */ jsxs(Table, { children: [
			/* @__PURE__ */ jsx(TableCaption, { children: TABLE_CAPTION$1 }),
			/* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
				/* @__PURE__ */ jsx(TableHead, { children: "Label" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Model" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Added" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Last used" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Status" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Action" })
			] }) }),
			/* @__PURE__ */ jsx(TableBody, { children: credentials.map((credential) => /* @__PURE__ */ jsxs(TableRow, { children: [
				/* @__PURE__ */ jsx(TableCell, { children: credential.label }),
				/* @__PURE__ */ jsx(TableCell, { children: credential.authenticatorModel ?? UNKNOWN_MODEL }),
				/* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(InstantDisplay, { instant: credential.createdAt }) }),
				/* @__PURE__ */ jsx(TableCell, { children: credential.lastUsedAt === null ? NEVER_USED : /* @__PURE__ */ jsx(InstantDisplay, { instant: credential.lastUsedAt }) }),
				/* @__PURE__ */ jsx(TableCell, { children: credential.disabledAt === null ? /* @__PURE__ */ jsx(Badge, {
					tone: "success",
					children: "Active"
				}) : /* @__PURE__ */ jsx(Badge, {
					tone: "destructive",
					children: "Disabled"
				}) }),
				/* @__PURE__ */ jsx(TableCell, { children: credential.disabledAt === null ? /* @__PURE__ */ jsx(Button, {
					variant: "destructive",
					type: "button",
					disabled: pending,
					"aria-label": `${DISABLE_LABEL$1} ${credential.label}`,
					onClick: handleDisable(credential),
					children: DISABLE_LABEL$1
				}) : null })
			] }, credential.id)) })
		] }) : null
	] }) });
}
//#endregion
//#region src/frontend/react/create-account-form.tsx
/** The text of the submit control, in both its enabled and its disabled state. */
const SUBMIT_LABEL = "Create account";
/**
* Creates a passwordless account for another person — the administrative provisioning an
* operator does before a key is enrolled (R5.1, R9.4).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <CreateAccountForm onCreated={() => reloadAccounts()} />
* </AuthenticationProvider>
* ```
*
* The account is passwordless by design — a key is its only credential, registered
* afterwards — so the only field is the address it is created at. Submitting creates it
* (`createAccount`, gated administratively by `ACCOUNT_CREATE_ANY` at the route); this
* component signs no one in, so the session is untouched and what follows a success is
* the product's decision through `onCreated`.
*
* Presentation is entirely Design_System components (R17.2, R13.7): `Card`, `Stack`,
* `FormField` with `Input`, `Alert` and `Button`. `noValidate` is set so the only rules
* deciding a submission are R13.10's — the address is judged by registration's own
* address rule, which is what the route applies (R5.1).
*/
function CreateAccountForm({ onCreated }) {
	const authentication = useAuthentication();
	const [email, setEmail] = useState("");
	const [errors, setErrors] = useState({});
	const [requestError, setRequestError] = useState(null);
	const [report, setReport] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	async function submit() {
		const outcome = validateCreateAccountFields({ email });
		setRequestError(null);
		setReport(null);
		if (!outcome.valid) {
			setErrors(outcome.errors);
			return;
		}
		setErrors({});
		setSubmitting(true);
		try {
			const created = await authentication.createAccount(outcome.value.email);
			setEmail("");
			setReport(`The account “${created.email}” has been created.`);
			onCreated?.();
		} catch (reason) {
			setRequestError(describeRejection(reason));
		} finally {
			setSubmitting(false);
		}
	}
	function handleSubmit(event) {
		event.preventDefault();
		if (submitting) return;
		submit();
	}
	function handleEmailChange(event) {
		setEmail(event.target.value);
	}
	return /* @__PURE__ */ jsx("form", {
		onSubmit: handleSubmit,
		noValidate: true,
		children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
			requestError === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "destructive",
				children: requestError
			}),
			report === null ? null : /* @__PURE__ */ jsx(Alert, {
				tone: "success",
				role: "status",
				children: report
			}),
			/* @__PURE__ */ jsx(FormField, {
				label: "Email address",
				error: errors.email ?? "",
				required: true,
				children: (control) => /* @__PURE__ */ jsx(Input, {
					...control,
					type: "email",
					autoComplete: "off",
					required: true,
					invalid: errors.email !== void 0,
					value: email,
					onChange: handleEmailChange
				})
			}),
			/* @__PURE__ */ jsx(Button, {
				type: "submit",
				disabled: submitting,
				children: SUBMIT_LABEL
			})
		] }) })
	});
}
//#endregion
//#region src/frontend/react/account-list.tsx
/** How many accounts a page carries. The route defaults an absent size to 25; this names it. */
const PAGE_SIZE = 25;
/** The text of the control that disables an account. */
const DISABLE_LABEL = "Disable";
/** The text of the control that re-enables a disabled account. */
const ENABLE_LABEL = "Enable";
/** The text of the control that reads the previous page. */
const PREVIOUS_LABEL = "Previous";
/** The text of the control that reads the next page. */
const NEXT_LABEL = "Next";
/** What is shown while a page read is in flight. */
const LOADING_MESSAGE = "Reading accounts…";
/** What is shown where the listing holds no account. */
const NO_ACCOUNTS_MESSAGE = "No accounts to show.";
/** What the table is captioned. */
const TABLE_CAPTION = "The accounts registered on the platform.";
/** What one completed disable reports. */
function disabledMessage(account) {
	return `The account “${account.email}” has been disabled.`;
}
/** What one completed enable reports. */
function enabledMessage(account) {
	return `The account “${account.email}” has been re-enabled.`;
}
/** The three lifecycle states a summary's two instants resolve to, for the status badge. */
function lifecycleOf(account) {
	if (account.closedAt !== null) return "closed";
	if (account.disabledAt !== null) return "disabled";
	return "active";
}
/**
* A paged listing of accounts, with controls that disable and re-enable one (R5.4, R5.2,
* R5.3, R9.4).
*
* ```tsx
* <AuthenticationProvider baseUrl="/authentication">
*   <AccountList />
* </AuthenticationProvider>
* ```
*
* A page is read on mount, and again when the operator pages or performs an action — a
* disable or an enable changes a row the person is looking at, so the page is re-read to
* show the new state rather than patched in place, which is the honest answer to "what
* does the listing hold now?". Disabling an account blocks its login and revokes its
* sessions (R5.2); re-enabling clears the block (R5.3); a closed account is terminal and
* carries neither control.
*
* Presentation is entirely Design_System components (R17.2): a `Table` of the
* material-free `AccountSummary`s, a `Badge` per lifecycle state, `InstantDisplay` for
* the creation instant, and `Button` controls. It declares no visual value of its own
* and no route or page layout.
*/
function AccountList({ onChanged }) {
	const authentication = useAuthentication();
	const [accounts, setAccounts] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [acting, setActing] = useState(null);
	const [actionError, setActionError] = useState(null);
	const [report, setReport] = useState(null);
	/**
	* A monotonic counter the actions bump to force a re-read of the current page.
	*
	* A dependency of the read effect rather than something it reads, so a completed
	* disable or enable re-runs the read exactly as a page change does — the honest way to
	* reflect a change to a row rather than patching the held list.
	*/
	const [revision, setRevision] = useState(0);
	/**
	* Reads the current page, on mount, on a page change, and after an action.
	*
	* `page` and `revision` are dependencies; `authentication` is captured and not
	* depended upon, as `SessionList`'s effect records.
	*/
	useEffect(() => {
		let current = true;
		setLoading(true);
		async function load() {
			try {
				const listing = await authentication.listAccounts({
					page,
					pageSize: PAGE_SIZE
				});
				if (current) {
					setAccounts(listing.items);
					setTotal(listing.total);
					setLoadError(null);
				}
			} catch (reason) {
				if (current) setLoadError(describeRejection(reason));
			} finally {
				if (current) setLoading(false);
			}
		}
		load();
		return () => {
			current = false;
		};
	}, [page, revision]);
	/** Whether an action is in flight, and therefore every control disabled. */
	const pending = acting !== null;
	/** Whether a further page exists past the current one. */
	const hasNext = page * PAGE_SIZE < total;
	async function act(account, perform, describe) {
		setActionError(null);
		setReport(null);
		setActing(account.id);
		try {
			await perform();
			setReport(describe(account));
			setRevision((seen) => seen + 1);
			onChanged?.();
		} catch (reason) {
			setActionError(describeRejection(reason));
		} finally {
			setActing(null);
		}
	}
	function handleDisable(account) {
		return () => {
			if (pending) return;
			act(account, () => authentication.disableAccount(account.id), disabledMessage);
		};
	}
	function handleEnable(account) {
		return () => {
			if (pending) return;
			act(account, () => authentication.enableAccount(account.id), enabledMessage);
		};
	}
	function handlePrevious() {
		if (pending || page <= 1) return;
		setPage((current) => current - 1);
	}
	function handleNext() {
		if (pending || !hasNext) return;
		setPage((current) => current + 1);
	}
	return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Stack, { children: [
		actionError === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "destructive",
			children: actionError
		}),
		loadError === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "destructive",
			children: loadError
		}),
		report === null ? null : /* @__PURE__ */ jsx(Alert, {
			tone: "success",
			role: "status",
			children: report
		}),
		loading ? /* @__PURE__ */ jsx(Alert, {
			tone: "informational",
			role: "status",
			children: LOADING_MESSAGE
		}) : null,
		!loading && loadError === null && accounts.length === 0 ? /* @__PURE__ */ jsx(Alert, {
			tone: "informational",
			role: "status",
			children: NO_ACCOUNTS_MESSAGE
		}) : null,
		accounts.length > 0 ? /* @__PURE__ */ jsxs(Table, { children: [
			/* @__PURE__ */ jsx(TableCaption, { children: TABLE_CAPTION }),
			/* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
				/* @__PURE__ */ jsx(TableHead, { children: "Email" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Created" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Verified" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Status" }),
				/* @__PURE__ */ jsx(TableHead, { children: "Action" })
			] }) }),
			/* @__PURE__ */ jsx(TableBody, { children: accounts.map((account) => {
				const lifecycle = lifecycleOf(account);
				return /* @__PURE__ */ jsxs(TableRow, { children: [
					/* @__PURE__ */ jsx(TableCell, { children: account.email }),
					/* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(InstantDisplay, { instant: account.createdAt }) }),
					/* @__PURE__ */ jsx(TableCell, { children: account.emailVerified ? /* @__PURE__ */ jsx(Badge, {
						tone: "success",
						children: "Verified"
					}) : /* @__PURE__ */ jsx(Badge, {
						tone: "warning",
						children: "Unverified"
					}) }),
					/* @__PURE__ */ jsx(TableCell, { children: lifecycle === "active" ? /* @__PURE__ */ jsx(Badge, {
						tone: "success",
						children: "Active"
					}) : lifecycle === "disabled" ? /* @__PURE__ */ jsx(Badge, {
						tone: "warning",
						children: "Disabled"
					}) : /* @__PURE__ */ jsx(Badge, {
						tone: "destructive",
						children: "Closed"
					}) }),
					/* @__PURE__ */ jsx(TableCell, { children: lifecycle === "active" ? /* @__PURE__ */ jsx(Button, {
						variant: "destructive",
						type: "button",
						disabled: pending,
						"aria-label": `${DISABLE_LABEL} ${account.email}`,
						onClick: handleDisable(account),
						children: DISABLE_LABEL
					}) : lifecycle === "disabled" ? /* @__PURE__ */ jsx(Button, {
						variant: "secondary",
						type: "button",
						disabled: pending,
						"aria-label": `${ENABLE_LABEL} ${account.email}`,
						onClick: handleEnable(account),
						children: ENABLE_LABEL
					}) : null })
				] }, account.id);
			}) })
		] }) : null,
		total > PAGE_SIZE ? /* @__PURE__ */ jsxs(Stack, {
			direction: "horizontal",
			gap: "small",
			align: "centre",
			children: [/* @__PURE__ */ jsx(Button, {
				variant: "secondary",
				type: "button",
				disabled: pending || page <= 1,
				onClick: handlePrevious,
				children: PREVIOUS_LABEL
			}), /* @__PURE__ */ jsx(Button, {
				variant: "secondary",
				type: "button",
				disabled: pending || !hasNext,
				onClick: handleNext,
				children: NEXT_LABEL
			})]
		}) : null
	] }) });
}
//#endregion
export { AccountList, AuthenticationProvider, AuthenticationStore, ChangeEmailForm, ChangePasswordForm, CloseAccountForm, CompletePasswordReset, CreateAccountForm, CredentialList, EnrolFirstKeyForm, LoginForm, PasskeyLoginButton, RegisterForm, RegisterKeyButton, RequestPasswordReset, SessionList, UserMenu, VerifyEmailPrompt, createAuthenticationStore, useAuthentication };

//# sourceMappingURL=frontend.mjs.map